import { S, hasGame } from '../core/game';
import type { Decor, Rect } from '../world/mapdef';
import { OUTLINE, block, ellipse, hash, rect, shadow, shade, sun, type Ctx } from './paint';
import { registerPropDrawers } from './propRegistry';
import { LIFE_ANIMATED, drawLife } from './streetLife';

/**
 * Prop drawers of the xinyi package: Taipei Main Station, Sun Yat-sen Memorial Hall, the Xinyi
 * department stores and skybridges, 騎樓 shop arcades, Four Four South Village, Elephant Mountain,
 * street life (taxis, scooters, the garbage truck) and the MRT station extras.
 */

const CJK = '"PingFang TC", "Noto Sans TC", "Noto Sans CJK TC", "Microsoft JhengHei", sans-serif';
const T = 16;

// ---------------------------------------------------------------------------
// Small helpers (same look as props4 / propsHualien)
// ---------------------------------------------------------------------------

function txt(c: Ctx, s: string, x: number, y: number, size: number, col: string, weight = 900, shadowCol?: string): void {
  c.font = `${weight} ${size}px ${CJK}`;
  c.textAlign = 'center';
  c.textBaseline = 'middle';
  if (shadowCol) {
    c.fillStyle = shadowCol;
    c.fillText(s, x + 0.4, y + 0.5);
  }
  c.fillStyle = col;
  c.fillText(s, x, y);
}

/** Vertical text, one character under the other. */
function vtxt(c: Ctx, s: string, x: number, y0: number, step: number, size: number, col: string, shadowCol?: string): void {
  [...s].forEach((ch, i) => txt(c, ch, x, y0 + i * step, size, col, 900, shadowCol));
}

function vgrad(c: Ctx, x: number, y: number, w: number, h: number, top: string, bottom: string): void {
  const g = c.createLinearGradient(0, y, 0, y + h);
  g.addColorStop(0, top);
  g.addColorStop(1, bottom);
  c.fillStyle = g;
  c.fillRect(x, y, w, h);
}

function poly(c: Ctx, col: string, pts: number[]): void {
  c.fillStyle = col;
  c.beginPath();
  c.moveTo(pts[0], pts[1]);
  for (let i = 2; i < pts.length; i += 2) c.lineTo(pts[i], pts[i + 1]);
  c.closePath();
  c.fill();
}

function line(c: Ctx, col: string, w: number, pts: number[]): void {
  c.strokeStyle = col;
  c.lineWidth = w;
  c.beginPath();
  c.moveTo(pts[0], pts[1]);
  for (let i = 2; i < pts.length; i += 2) c.lineTo(pts[i], pts[i + 1]);
  c.stroke();
}

const isNight = () => sun.alpha < 0.8;
const H = (h: number, m = 0) => h * 60 + m;
const timeNow = () => (hasGame() ? S().time : H(12));

/**
 * Offscreen cache for big static drawings (outdoor maps redraw every prop each frame). The bitmap is
 * built once per zoom level; `a..b` draws only that horizontal slice (for landmarks split in pieces).
 */
const bitmaps = new Map<string, { cv: HTMLCanvasElement; s: number }>();
function art(c: Ctx, key: string, x0: number, y0: number, w: number, h: number, draw: (cc: Ctx) => void, a = -1e9, b = 1e9): void {
  const m = c.getTransform();
  const s = Math.max(1, Math.round(Math.hypot(m.a, m.b) * 2) / 2);
  let e = bitmaps.get(key);
  if (!e || e.s !== s) {
    const cv = document.createElement('canvas');
    cv.width = Math.ceil(w * s);
    cv.height = Math.ceil(h * s);
    const cc = cv.getContext('2d')!;
    cc.scale(s, s);
    cc.translate(-x0, -y0);
    draw(cc);
    e = { cv, s };
    bitmaps.set(key, e);
  }
  const top = Math.max(a, y0), bot = Math.min(b, y0 + h);
  if (bot <= top) return;
  c.drawImage(e.cv, 0, (top - y0) * s, e.cv.width, (bot - top) * s, x0, top, w, bot - top);
}

/** Slice of a tall landmark split by the map into 90 px pieces (variant = style * 10 + piece). */
function piece(d: Decor): { style: number; bottom: number; a: number; b: number } {
  const v = d.variant ?? 0;
  const p = v % 10;
  return { style: Math.floor(v / 10), bottom: d.y + 90 * p, a: d.y - 90, b: d.y + (p === 0 ? 10 : 0) };
}

const NEON = ['#ff5a7a', '#5ad8ff', '#ffd34e', '#7affa0', '#c58cff'];

// ---------------------------------------------------------------------------
// Taipei Main Station: a big box under a green-tiled double-eaved roof
// ---------------------------------------------------------------------------

function glazedTiles(c: Ctx, pts: number[], base: string, x0: number, x1: number, y0: number, y1: number, step = 3): void {
  c.save();
  c.beginPath();
  c.moveTo(pts[0], pts[1]);
  for (let i = 2; i < pts.length; i += 2) c.lineTo(pts[i], pts[i + 1]);
  c.closePath();
  c.clip();
  vgrad(c, x0, y0, x1 - x0, y1 - y0, shade(base, 0.18), shade(base, -0.12));
  for (let x = x0; x < x1; x += step) rect(c, 'rgba(0,0,0,0.16)', x, y0, 0.7, y1 - y0);
  for (let x = x0 + step / 2; x < x1; x += step) rect(c, 'rgba(255,255,255,0.12)', x, y0, 0.5, y1 - y0);
  for (let y = y0 + 4; y < y1; y += 5) rect(c, 'rgba(0,0,0,0.08)', x0, y, x1 - x0, 0.5);
  c.restore();
}

function upturn(c: Ctx, col: string, x: number, y: number, dir: number, size = 6): void {
  poly(c, col, [x, y, x + dir * size, y - size * 0.9, x + dir * size * 0.4, y - size * 0.25, x - dir * 2, y - 2]);
}

function station(c: Ctx, cx: number, by: number, night: boolean): void {
  const w = 412, L = cx - w / 2, R = cx + w / 2;
  shadow(c, cx, by, w / 2 + 6, 5, 0.3);
  // Granite plinth and the main body with its tall window bays.
  block(c, L - 2, by - 8, w + 4, 8, '#b8ab94', '#d0c4ac', '#8e8270');
  block(c, L + 2, by - 62, w - 4, 54, '#d9c8a6', '#ecdcbc', '#b8a684');
  for (let bx = L + 8; bx < R - 12; bx += 16) {
    if (Math.abs(bx + 6 - cx) < 46) continue;
    rect(c, '#e8d8b8', bx - 1, by - 58, 2, 50);
    const lit = night && hash(bx, 3) < 0.7;
    vgrad(c, bx + 2, by - 56, 9, 21, lit ? '#ffe2a0' : '#4a5a62', lit ? '#e8b060' : '#26323a');
    vgrad(c, bx + 2, by - 32, 9, 22, lit ? '#ffe8b0' : '#4a5a62', lit ? '#f0c070' : '#26323a');
    rect(c, '#6a4a2e', bx + 2, by - 35, 9, 2);
    rect(c, 'rgba(255,255,255,0.22)', bx + 3, by - 55, 1.2, 18);
  }
  // Central entrance hall: a tall recess with a glass curtain wall (the portal stands in front).
  block(c, cx - 46, by - 62, 92, 54, '#c8b490', '#dcc8a4', '#a08c6c');
  vgrad(c, cx - 40, by - 58, 80, 50, night ? '#ffe6a8' : '#5a7488', night ? '#e0a860' : '#2c3c48');
  for (let i = 0; i <= 8; i++) rect(c, '#8a7a62', cx - 40 + i * 10, by - 58, 1, 50);
  for (let j = 0; j < 4; j++) rect(c, '#8a7a62', cx - 40, by - 58 + j * 12.5, 80, 0.8);
  // Lower eave: green glazed tiles with a red bracket band and upturned corners.
  rect(c, '#8a2a1e', L + 2, by - 64, w - 4, 3);
  for (let x = L + 6; x < R - 4; x += 6) rect(c, '#2f8a8a', x, by - 64, 3, 2);
  const e1 = [L - 8, by - 62, R + 8, by - 62, R - 2, by - 74, L + 2, by - 74];
  glazedTiles(c, e1, '#3f8a5a', L - 10, R + 10, by - 75, by - 61);
  rect(c, '#24563a', L - 8, by - 63, w + 16, 1.6);
  upturn(c, '#3f8a5a', L - 8, by - 62, -1);
  upturn(c, '#3f8a5a', R + 8, by - 62, 1);
  // Upper storey with the name board.
  block(c, L + 36, by - 96, w - 72, 23, '#d2c09c', '#e4d4b4', '#a89878');
  for (let bx = L + 44; bx < R - 44; bx += 12) {
    if (Math.abs(bx + 3 - cx) < 58) continue;
    rect(c, night && hash(bx, 7) < 0.6 ? '#ffe2a0' : '#3a4a52', bx, by - 91, 6, 12);
  }
  block(c, cx - 54, by - 95, 108, 20, '#5a1e14', '#7a2e20', '#3a120c');
  rect(c, '#d9b450', cx - 52, by - 93.5, 104, 0.6);
  rect(c, '#d9b450', cx - 52, by - 77.5, 104, 0.6);
  txt(c, '臺北車站', cx, by - 88.5, 12, '#ffd56a', 900, 'rgba(0,0,0,0.5)');
  txt(c, 'TAIPEI MAIN STATION', cx, by - 80, 3.6, '#fff3d0', 700);
  // Main hip roof with its ridge and the curled ridge ends.
  const r1 = [L + 16, by - 94, R - 16, by - 94, R - 96, by - 126, L + 96, by - 126];
  glazedTiles(c, r1, '#4f9a68', L + 10, R - 10, by - 127, by - 93);
  rect(c, '#24563a', L + 14, by - 95, w - 28, 1.8);
  line(c, '#2f6a44', 1.4, [L + 16, by - 94, L + 96, by - 126]);
  line(c, '#2f6a44', 1.4, [R - 16, by - 94, R - 96, by - 126]);
  upturn(c, '#4f9a68', L + 14, by - 94, -1, 8);
  upturn(c, '#4f9a68', R - 14, by - 94, 1, 8);
  block(c, L + 92, by - 131, w - 184, 6, '#2f6a44', '#4f8a5a', '#1f4a2e');
  for (const [x, dir] of [[L + 92, -1], [R - 92, 1]] as const) {
    poly(c, '#2f6a44', [x, by - 125, x + dir * 2, by - 134, x + dir * 6, by - 136, x + dir * 4, by - 131, x + dir * 3, by - 126]);
  }
  if (night) {
    rect(c, 'rgba(255,230,160,0.5)', L - 6, by - 62.5, w + 12, 0.8);
    rect(c, 'rgba(255,230,160,0.5)', L + 14, by - 94.5, w - 28, 0.8);
  }
}

// ---------------------------------------------------------------------------
// Sun Yat-sen Memorial Hall: yellow roof with the swept-up central eave
// ---------------------------------------------------------------------------

const YEL = '#e8b830';

function sysHall(c: Ctx, cx: number, by: number, night: boolean): void {
  const w = 340, L = cx - w / 2, R = cx + w / 2;
  shadow(c, cx, by, w / 2 + 4, 5, 0.3);
  // Platform and the wide front stairs.
  block(c, L + 4, by - 12, w - 8, 12, '#d6d0c4', '#ece6da', '#a8a294');
  for (let k = 1; k < 4; k++) rect(c, 'rgba(0,0,0,0.12)', cx - 60, by - 12 + k * 3, 120, 0.6);
  block(c, cx - 62, by - 12, 124, 12, '#e4ded2', '#f6f0e4', '#b8b2a4');
  for (let k = 1; k < 4; k++) rect(c, 'rgba(0,0,0,0.14)', cx - 60, by - 12 + k * 3, 120, 0.6);
  // Cream walls and the colonnade.
  block(c, L + 16, by - 64, w - 32, 52, '#efe6d2', '#fff8e8', '#c8bea8');
  const cols = 14;
  for (let i = 0; i <= cols; i++) {
    const x = L + 22 + (i * (w - 44)) / cols;
    if (i < cols) {
      const x2 = L + 22 + ((i + 1) * (w - 44)) / cols;
      const mid = (x + x2) / 2;
      const centre = Math.abs(mid - cx) < 14;
      const lit = night && (centre || hash(i, 11) < 0.6);
      vgrad(c, x + 4, by - 58, x2 - x - 8, 46, centre ? '#8a2a1e' : lit ? '#ffe2a0' : '#6a5a48', centre ? '#6a1a10' : lit ? '#e8a860' : '#3a3026');
      if (centre) for (let k = 0; k < 5; k++) for (let j = 0; j < 3; j++) ellipse(c, '#e8c860', x + 7 + j * 3.5, by - 50 + k * 8, 0.6, 0.6);
      else for (let k = 1; k < 4; k++) rect(c, 'rgba(0,0,0,0.2)', x + 4, by - 58 + k * 11.5, x2 - x - 8, 0.6);
    }
    block(c, x - 3.5, by - 62, 7, 50, '#dcd8d0', '#f8f4ec', '#a8a49c');
    rect(c, '#b8b4ac', x - 4, by - 14, 8, 2);
  }
  // Red-and-teal bracket band under the eaves.
  rect(c, '#8a2a1e', L + 12, by - 70, w - 24, 6);
  for (let x = L + 14; x < R - 12; x += 7) {
    rect(c, '#2f8a8a', x, by - 69, 3.5, 2.4);
    rect(c, '#e8c860', x + 1.2, by - 66.4, 1, 1);
  }
  // Main hip roof.
  const r1 = [L - 6, by - 70, R + 6, by - 70, R - 66, by - 118, L + 66, by - 118];
  glazedTiles(c, r1, YEL, L - 8, R + 8, by - 119, by - 69);
  rect(c, '#a87810', L - 6, by - 71, w + 12, 1.8);
  upturn(c, YEL, L - 6, by - 70, -1, 9);
  upturn(c, YEL, R + 6, by - 70, 1, 9);
  line(c, '#b88a18', 1.2, [L - 4, by - 71, L + 66, by - 118]);
  line(c, '#b88a18', 1.2, [R + 4, by - 71, R - 66, by - 118]);
  // The famous swept-up central section: its eave rises in a wide arc above the entrance.
  const half = 118;
  const lowY = (k: number) => by - 74 - Math.pow(Math.cos((k * Math.PI) / 2), 1.4) * 40;
  const topY = (k: number) => by - 124 - Math.pow(Math.cos((k * Math.PI) / 2), 1.6) * 28;
  // The gable wall glimpsed under the arc, with its plaque.
  c.fillStyle = '#e8dcc4';
  c.beginPath();
  for (let k = -1; k <= 1.0001; k += 0.05) c.lineTo(cx + k * (half - 10), lowY(k) + 2);
  c.lineTo(cx + half - 10, by - 70);
  c.lineTo(cx - half + 10, by - 70);
  c.closePath();
  c.fill();
  for (let x = cx - 80; x < cx + 80; x += 7) rect(c, '#2f8a8a', x, lowY((x - cx) / half) + 2.5, 3.5, 2);
  block(c, cx - 30, by - 98, 60, 14, '#1f3a6a', '#2f4f8a', '#122444');
  rect(c, '#e8c860', cx - 28.5, by - 96.5, 57, 0.6);
  txt(c, '國父紀念館', cx, by - 91, 8.5, '#ffd56a', 900, 'rgba(0,0,0,0.5)');
  for (let k = 0; k < 3; k++) ellipse(c, '#c0392b', cx - 40 + k * 40, by - 76, 2.2, 2.6);
  // The curved roof sheet itself.
  c.save();
  c.beginPath();
  for (let k = -1; k <= 1.0001; k += 0.04) c.lineTo(cx + k * half, lowY(k));
  for (let k = 1; k >= -1.0001; k -= 0.04) c.lineTo(cx + k * (half - 46), topY(k));
  c.closePath();
  c.clip();
  vgrad(c, cx - half, by - 150, half * 2, 84, shade(YEL, 0.2), shade(YEL, -0.1));
  for (let x = cx - half; x < cx + half; x += 3) rect(c, 'rgba(120,80,0,0.2)', x, by - 150, 0.7, 84);
  c.restore();
  c.strokeStyle = '#a87810';
  c.lineWidth = 1.8;
  c.beginPath();
  for (let k = -1; k <= 1.0001; k += 0.04) c.lineTo(cx + k * half, lowY(k));
  c.stroke();
  c.strokeStyle = 'rgba(255,240,180,0.7)';
  c.lineWidth = 0.6;
  c.beginPath();
  for (let k = -1; k <= 1.0001; k += 0.04) c.lineTo(cx + k * half, lowY(k) - 1.6);
  c.stroke();
  // Flicked tips of the swept eave and the ridge.
  upturn(c, YEL, cx - half, lowY(-1), -1, 10);
  upturn(c, YEL, cx + half, lowY(1), 1, 10);
  c.strokeStyle = '#a87810';
  c.lineWidth = 3;
  c.beginPath();
  for (let k = -1; k <= 1.0001; k += 0.05) c.lineTo(cx + k * (half - 46), topY(k));
  c.stroke();
  for (const dir of [-1, 1]) {
    const x = cx + dir * (half - 46), y = topY(dir);
    poly(c, '#a87810', [x, y + 1, x + dir * 3, y - 7, x + dir * 7, y - 9, x + dir * 5, y - 4, x + dir * 4, y]);
  }
  if (night) {
    c.strokeStyle = 'rgba(255,230,150,0.6)';
    c.lineWidth = 0.8;
    c.beginPath();
    for (let k = -1; k <= 1.0001; k += 0.04) c.lineTo(cx + k * half, lowY(k) + 1);
    c.stroke();
  }
}

// ---------------------------------------------------------------------------
// Xinyi department stores (with animated screens)
// ---------------------------------------------------------------------------

interface Dept {
  w: number;
  h: number;
  screen?: { x: number; y: number; w: number; h: number };
}
const DEPTS: Dept[] = [
  { w: 128, h: 116, screen: { x: -52, y: -106, w: 104, h: 46 } },
  { w: 96, h: 108 },
  { w: 80, h: 112, screen: { x: -32, y: -104, w: 34, h: 62 } },
];

function deptFacade(c: Ctx, style: number, cx: number, by: number, night: boolean): void {
  const D = DEPTS[style];
  const L = cx - D.w / 2, R = cx + D.w / 2;
  shadow(c, cx, by, D.w / 2 + 4, 4, 0.3);
  if (style === 0) {
    block(c, L, by - D.h, D.w, D.h, '#c9cdd2', '#e4e8ec', '#9aa0a8');
    for (let y = by - D.h + 6; y < by - 34; y += 12) {
      vgrad(c, L + 4, y, D.w - 8, 6, night ? '#ffe6b0' : '#4a6070', night ? '#e8b878' : '#2a3a48');
      for (let x = L + 4; x < R - 4; x += 8) rect(c, '#8a9098', x, y, 0.8, 6);
    }
    const s = D.screen!;
    block(c, cx + s.x - 3, by + s.y - 3, s.w + 6, s.h + 6, '#14161a', '#2a2e34', '#0a0b0e');
    block(c, cx - 36, by - 56, 72, 11, '#20242a', '#343a42');
    txt(c, '星空百貨', cx - 12, by - 50.4, 7, '#9ad8ff', 900);
    txt(c, 'GALAXY', cx + 22, by - 50.4, 4.2, '#ffffff', 800);
    if (night) ellipse(c, 'rgba(120,200,255,0.25)', cx, by - 50, 40, 8);
  } else if (style === 1) {
    block(c, L, by - D.h, D.w, D.h, '#5a3a24', '#7a5232', '#3a2416');
    for (let x = L + 2; x < R - 1; x += 3) rect(c, 'rgba(255,220,170,0.12)', x, by - D.h + 2, 1, D.h - 4);
    for (const wy of [by - 96, by - 64]) {
      block(c, L + 14, wy, D.w - 22, 26, '#3a2416');
      vgrad(c, L + 15, wy + 1, D.w - 24, 24, night ? '#ffe2a8' : '#f2d7a0', night ? '#f0b870' : '#d8b880');
      for (let sy = wy + 7; sy < wy + 25; sy += 6) {
        rect(c, '#6a4a2e', L + 15, sy, D.w - 24, 1);
        for (let bx = L + 16; bx < R - 10; bx += 2.2) {
          const hh = 3 + hash(bx, sy) * 2;
          rect(c, ['#c0392b', '#2f5fa8', '#57c278', '#f2c94c', '#8a3aa0', '#f4f1ea'][Math.floor(hash(bx, sy, 3) * 6)], bx, sy - hh, 1.6, hh);
        }
      }
      rect(c, 'rgba(255,255,255,0.25)', L + 16, wy + 2, 1.5, 20);
    }
    // Vertical banner and the name over the door.
    block(c, L + 2, by - 104, 11, 44, '#1d1a18', '#2e2a26');
    vtxt(c, '書林', L + 7.5, by - 96, 11, 8, '#f2e2c0');
    txt(c, 'BOOK', L + 7.5, by - 72, 2.6, '#c8a878', 800);
    txt(c, 'TREE', L + 7.5, by - 68.5, 2.6, '#c8a878', 800);
    block(c, cx - 30, by - 36, 60, 9, '#1d1a18', '#2e2a26');
    txt(c, 'Book Tree 書林', cx, by - 31.4, 5, '#f2e2c0', 800);
    for (let i = 0; i < 4; i++) ellipse(c, '#3f8a3a', L + 18 + i * 22, by - D.h, 7, 3);
  } else {
    block(c, L, by - D.h, D.w, D.h, '#f4f2ee', '#ffffff', '#c8c4bc');
    for (let y = by - D.h + 8; y < by - 36; y += 10) rect(c, 'rgba(120,130,150,0.25)', L + 2, y, D.w - 4, 0.8);
    const s = D.screen!;
    block(c, cx + s.x - 2, by + s.y - 2, s.w + 4, s.h + 4, '#14161a', '#2a2e34');
    // Pink neon name running down the right side.
    block(c, R - 22, by - 104, 16, 60, '#2a1a2a', '#3a2a3a');
    vtxt(c, '月光廣場', R - 14, by - 96, 13, 10, night ? '#ffb0e0' : '#ff6ac0', night ? 'rgba(255,90,200,0.8)' : undefined);
    if (night) ellipse(c, 'rgba(255,100,200,0.22)', R - 14, by - 74, 14, 34);
    rect(c, '#ff6ac0', L + 2, by - 38, D.w - 4, 1.4);
  }
  // Shared ground floor: canopy and glass shop front with the doors in the middle.
  block(c, L - 2, by - 33, D.w + 4, 4, '#e8ecef', '#ffffff', '#a8b0b8');
  vgrad(c, L + 3, by - 29, D.w - 6, 29, night ? '#ffeac0' : '#4a6474', night ? '#e8c080' : '#22303a');
  for (let x = L + 3; x < R - 3; x += 12) rect(c, '#c8d0d6', x, by - 29, 0.8, 29);
  // Mannequins and displays in the windows.
  for (let x = L + 9; x < R - 6; x += 24) {
    if (Math.abs(x - cx) < 14) continue;
    ellipse(c, '#e8e0d8', x, by - 22, 1.6, 1.8);
    rect(c, ['#e2493b', '#3a8fd9', '#f2c94c', '#2a2a30'][Math.floor(hash(x, style) * 4)], x - 2.5, by - 20, 5, 9);
    rect(c, '#2a2a30', x - 0.4, by - 11, 0.8, 6);
  }
  block(c, cx - 11, by - 27, 22, 27, '#8a9aa6', '#aab8c2');
  vgrad(c, cx - 9, by - 25, 18, 25, night ? '#fff0c8' : '#5a7a8a', night ? '#f0c888' : '#2a3a48');
  rect(c, '#c8d0d6', cx - 0.4, by - 25, 0.8, 25);
  rect(c, 'rgba(255,255,255,0.3)', cx - 7, by - 24, 1.4, 18);
}

/** The LED screens: three cheap rotating scenes. */
function screen(c: Ctx, x: number, y: number, w: number, h: number, t: number, seed: number): void {
  const scene = Math.floor(t / 5 + seed) % 3;
  c.save();
  c.beginPath();
  c.rect(x, y, w, h);
  c.clip();
  if (scene === 0) {
    vgrad(c, x, y, w, h, '#2a5ae0', '#8ad0ff');
    const big = Math.min(w, h) * 0.32;
    txt(c, w > h ? '台北 TAIPEI' : '台北', x + w / 2, y + h * 0.42, w > h ? big * 0.7 : big, '#ffffff', 900, 'rgba(0,0,60,0.4)');
    for (let i = 0; i < 6; i++) {
      const k = (t * 0.4 + i / 6) % 1;
      ellipse(c, 'rgba(255,255,255,0.8)', x + ((i * 37) % w), y + h - k * h, 0.8, 0.8);
    }
    if (h > 40) txt(c, '101', x + w / 2, y + h * 0.75, big * 0.8, '#ffd34e', 900);
  } else if (scene === 1) {
    vgrad(c, x, y, w, h, '#ffb070', '#ff7a3a');
    const cw = Math.min(w * 0.3, 18), ch = Math.min(h * 0.7, 30);
    const cx = x + (w > h ? w * 0.28 : w / 2), cy = y + h * 0.18;
    block(c, cx - cw / 2, cy, cw, ch, '#fff8f0');
    rect(c, '#c49a6c', cx - cw / 2 + 1, cy + ch * 0.35, cw - 2, ch * 0.6);
    for (let k = 0; k < 5; k++) ellipse(c, '#2a1810', cx - cw / 2 + 2.5 + k * ((cw - 5) / 4), cy + ch - 3, 1.1, 1.1);
    rect(c, '#e2493b', cx + 1, cy - 6, 1.4, 10);
    if (w > h) {
      txt(c, '珍珠奶茶', x + w * 0.66, y + h * 0.4, h * 0.2, '#7a2a10', 900);
      txt(c, 'BUBBLE TEA', x + w * 0.66, y + h * 0.65, h * 0.11, '#ffffff', 900);
    }
  } else {
    rect(c, '#140a34', x, y, w, h);
    for (let i = 0; i < 3; i++) {
      const k = (t * 0.7 + i / 3) % 1;
      const bx = x + w * (0.25 + i * 0.25), byy = y + h * (0.3 + (i % 2) * 0.15);
      const r = 3 + k * Math.min(w, h) * 0.3;
      for (let a = 0; a < 10; a++) ellipse(c, NEON[(i + a) % NEON.length], bx + Math.cos(a * 0.63) * r, byy + Math.sin(a * 0.63) * r, 0.9, 0.9);
    }
    txt(c, '2027', x + w / 2, y + h * 0.8, Math.min(w * 0.22, h * 0.25), '#ffd34e', 900);
  }
  // Scan lines and glass glare.
  for (let yy = y; yy < y + h; yy += 2) rect(c, 'rgba(0,0,0,0.08)', x, yy, w, 0.5);
  rect(c, 'rgba(255,255,255,0.12)', x, y, w, 1);
  c.restore();
}

// ---------------------------------------------------------------------------
// 騎樓 arcade rows: shopfronts at the back, pillars and signboards in front
// ---------------------------------------------------------------------------

interface Shop {
  tiles: number;
  kind: 'conv' | 'tea' | 'snack' | 'glasses' | 'pharmacy' | 'books';
  name: string;
  en: string;
  bg: string;
  wall: string;
}
const SHOPS: Shop[] = [
  { tiles: 6, kind: 'conv', name: '好鄰居', en: 'GOOD NEIGHBOR MART', bg: '#1f7a9a', wall: '#e8e4dc' },
  { tiles: 4, kind: 'tea', name: '泡泡熊茶飲', en: 'BUBBLE BEAR', bg: '#e2493b', wall: '#f0d8c8' },
  { tiles: 5, kind: 'snack', name: '阿明小吃', en: 'SNACKS', bg: '#f0a020', wall: '#d8c8b0' },
  { tiles: 4, kind: 'glasses', name: '明亮眼鏡', en: 'OPTICAL', bg: '#2f5fa8', wall: '#d0d8e0' },
  { tiles: 4, kind: 'pharmacy', name: '健康藥局', en: 'PHARMACY', bg: '#2f9a5a', wall: '#e4ecdc' },
  { tiles: 5, kind: 'books', name: '文具書局', en: 'STATIONERY', bg: '#8a3aa0', wall: '#e8dcc8' },
];

function shopBack(c: Ctx, L: number, by: number, night: boolean): void {
  let x = L;
  for (const s of SHOPS) {
    const w = s.tiles * T;
    vgrad(c, x, by - 22, w, 22, night ? '#fff0c8' : shade(s.wall, -0.15), night ? '#f0d098' : shade(s.wall, -0.3));
    switch (s.kind) {
      case 'conv':
        for (let k = 0; k < 2; k++) {
          rect(c, '#c8ccd0', x + 3, by - 18 + k * 7, w - 22, 1);
          for (let i = 0; i < w - 24; i += 2.5) rect(c, ['#e2493b', '#3a8fd9', '#f2c94c', '#57c278'][Math.floor(hash(x + i, k) * 4)], x + 3.5 + i, by - 21 + k * 7, 1.6, 3);
        }
        vgrad(c, x + w - 18, by - 22, 14, 22, '#bfe4ee', '#8ac0d0');
        rect(c, '#9aa6ae', x + w - 11.4, by - 22, 0.8, 22);
        block(c, x + 4, by - 8, 18, 8, '#e8ecef', '#ffffff');
        break;
      case 'tea':
        for (let i = 0; i < 3; i++) block(c, x + 4 + i * 19, by - 21, 17, 7, '#ffd34e', '#fff0a0');
        for (let i = 0; i < 3; i++) for (let k = 0; k < 3; k++) rect(c, '#7a2a10', x + 6 + i * 19, by - 19.5 + k * 1.8, 12, 0.5);
        block(c, x + 2, by - 10, w - 4, 10, '#e2493b', '#ff7a6a', '#a8261c');
        rect(c, '#ffffff', x + 2, by - 7, w - 4, 1.2);
        for (let i = 0; i < 7; i++) {
          block(c, x + 6 + i * 8, by - 15, 4, 5, 'rgba(255,250,240,0.95)');
          rect(c, i % 2 ? '#c49a6c' : '#9ad06a', x + 6.5 + i * 8, by - 13, 3, 2.6);
        }
        break;
      case 'snack':
        for (let k = 0; k < 3; k++) block(c, x + 6, by - 12 - k * 4, 14, 4, '#c8a060', '#e0c080', '#8a6a3a');
        ellipse(c, 'rgba(255,255,255,0.4)', x + 13, by - 25, 5, 2);
        for (let i = 0; i < 2; i++) {
          block(c, x + 30 + i * 22, by - 9, 14, 3, '#f4f1ea', '#ffffff');
          rect(c, '#9a948a', x + 31 + i * 22, by - 6, 1, 6);
          rect(c, '#9a948a', x + 42 + i * 22, by - 6, 1, 6);
          ellipse(c, '#e2493b', x + 28 + i * 22, by - 3, 2.4, 1);
        }
        break;
      case 'glasses':
        block(c, x + 4, by - 18, w - 8, 12, '#2a2e36', '#3a3e46');
        for (let i = 0; i < 6; i++) {
          const gx = x + 9 + (i % 3) * 18, gy = by - 15 + Math.floor(i / 3) * 5;
          c.strokeStyle = ['#e8c860', '#c8cdd2', '#e2493b'][i % 3];
          c.lineWidth = 0.7;
          c.beginPath();
          c.arc(gx - 2.4, gy, 1.8, 0, Math.PI * 2);
          c.moveTo(gx + 4.2, gy);
          c.arc(gx + 2.4, gy, 1.8, 0, Math.PI * 2);
          c.stroke();
        }
        break;
      case 'pharmacy':
        for (let k = 0; k < 3; k++) {
          rect(c, '#ffffff', x + 3, by - 20 + k * 6, w - 6, 1);
          for (let i = 0; i < w - 8; i += 3) block(c, x + 4 + i, by - 24 + k * 6, 2.4, 3.6, ['#f4f1ea', '#9fd3f0', '#ffb0c0', '#c8e8a0'][Math.floor(hash(x + i, k, 4) * 4)]);
        }
        break;
      case 'books':
        for (let k = 0; k < 2; k++) {
          rect(c, '#6a4a2e', x + 3, by - 13 + k * 7, w - 6, 1);
          for (let i = 0; i < w - 8; i += 2) rect(c, ['#c0392b', '#2f5fa8', '#57c278', '#f2c94c', '#ff9fc0'][Math.floor(hash(x + i, k, 5) * 5)], x + 4 + i, by - 17 + k * 7, 1.5, 4);
        }
        break;
    }
    rect(c, 'rgba(0,0,0,0.2)', x, by - 22, 0.8, 22);
    x += w;
  }
}

function arcade(c: Ctx, L: number, by: number, w: number, night: boolean): void {
  const ceil = by - 34, top = by - 74;
  let x = L;
  SHOPS.forEach((s, si) => {
    const sw = s.tiles * T;
    const tile = ['#e8e0d0', '#d8c8b8', '#c8d8d8', '#e8d0c8', '#d0d4dc', '#e4dcc4'][si];
    block(c, x, top, sw, 40, tile, shade(tile, 0.2), shade(tile, -0.2));
    // Mosaic tile texture.
    for (let yy = top + 2; yy < ceil - 14; yy += 2.5) rect(c, 'rgba(0,0,0,0.05)', x + 1, yy, sw - 2, 0.4);
    // Upper-floor windows with white iron grilles (鐵窗) and AC units.
    const nWin = Math.max(1, Math.floor(sw / 24));
    for (let i = 0; i < nWin; i++) {
      const wx = x + (sw / nWin) * i + (sw / nWin - 14) / 2;
      const lit = night && hash(x + i, 9) < 0.65;
      vgrad(c, wx, top + 4, 14, 13, lit ? '#ffe2a0' : '#3a4a56', lit ? '#e8b070' : '#24303a');
      for (let k = 1; k < 5; k++) rect(c, '#f4f4f0', wx + k * 2.8, top + 3, 0.6, 15);
      rect(c, '#f4f4f0', wx - 1, top + 3, 16, 0.8);
      rect(c, '#f4f4f0', wx - 1, top + 17, 16, 0.8);
      if (hash(x, i, 2) < 0.6) {
        block(c, wx + 15, top + 12, 9, 6, '#e8ecef', '#ffffff', '#b8c0c8');
        ellipse(c, '#9aa6ae', wx + 19.5, top + 15, 2, 2);
      }
      if (hash(x, i, 6) < 0.4) for (let k = 0; k < 3; k++) ellipse(c, '#3f8a3a', wx + 2 + k * 5, top + 18, 2.4, 1.6);
    }
    // The big signboard band over the arcade.
    const sy = ceil - 14;
    block(c, x + 1, sy, sw - 2, 13, night ? shade(s.bg, 0.15) : s.bg, shade(s.bg, 0.3), shade(s.bg, -0.3));
    txt(c, s.name, x + sw / 2, sy + 5.4, s.name.length > 3 ? 7 : 8, '#fff6dc', 900, 'rgba(0,0,0,0.4)');
    txt(c, s.en, x + sw / 2, sy + 10.6, 2.8, '#ffffff', 800);
    if (s.kind === 'pharmacy') {
      rect(c, '#ffffff', x + 6, sy + 3, 6, 2);
      rect(c, '#ffffff', x + 8, sy + 1, 2, 6);
    }
    if (night) rect(c, 'rgba(255,255,255,0.18)', x + 2, sy + 1, sw - 4, 2);
    // A tall vertical sign jutting out at some shops.
    if (si % 2 === 1) {
      const vx = x + sw - 6;
      block(c, vx - 4, top - 6, 8, 34, '#c0392b', '#e05a48', '#8a2a1e');
      vtxt(c, s.name.slice(0, 3), vx, top, 9, 6, '#fff3b0', 'rgba(0,0,0,0.4)');
    }
    x += sw;
  });
  // Rooftop lip, water tanks and a TV aerial: the Taipei skyline in miniature.
  block(c, L, top - 6, w, 7, '#b8b4ac', '#d8d4cc', '#8a8680');
  for (let i = 0; i < 5; i++) {
    const tx = L + 20 + i * 92 + (i % 2) * 18;
    if (tx > L + w - 12) break;
    block(c, tx - 6, top - 15, 12, 9, '#c8ccd0', '#eef0f2', '#9aa0a6');
    rect(c, '#8a9096', tx - 6, top - 11, 12, 0.6);
    rect(c, '#6a7078', tx - 5, top - 7, 1, 2);
    rect(c, '#6a7078', tx + 4, top - 7, 1, 2);
  }
  line(c, '#55606a', 0.5, [L + 140, top - 6, L + 140, top - 16, L + 134, top - 16, L + 146, top - 16]);
  // Soffit shadow and the arcade pillars.
  rect(c, 'rgba(0,0,0,0.35)', L, ceil, w, 2);
  for (let px = L; px <= L + w + 0.1; px += 32) {
    const x0 = Math.min(Math.max(px - 3, L), L + w - 6);
    block(c, x0, ceil, 6, 34, '#e4d8c4', '#f4ecdc', '#b8ac98');
    for (let k = 0; k < 6; k++) rect(c, 'rgba(0,0,0,0.06)', x0 + 1, ceil + 3 + k * 5, 4, 0.5);
    rect(c, '#8a7a66', x0, by - 4, 6, 4);
  }
}

// ---------------------------------------------------------------------------
// Four Four South Village houses (眷村)
// ---------------------------------------------------------------------------

function juancun(c: Ctx, cx: number, by: number, v: number, night: boolean): void {
  const w = 78, L = cx - w / 2, R = cx + w / 2;
  shadow(c, cx, by, w / 2 + 2, 3.5, 0.3);
  const cafe = v === 9;
  // Grey-brick walls (the café is whitewashed on the lower half).
  block(c, L + 2, by - 27, w - 4, 27, '#8e8c86', '#a8a6a0', '#6e6c66');
  for (let yy = by - 25; yy < by - 1; yy += 3) {
    rect(c, 'rgba(50,48,44,0.35)', L + 3, yy, w - 6, 0.5);
    const off = ((yy / 3) | 0) % 2 ? 3 : 0;
    for (let xx = L + 4 + off; xx < R - 3; xx += 6) rect(c, 'rgba(50,48,44,0.3)', xx, yy, 0.5, 3);
  }
  if (cafe) block(c, L + 2, by - 13, w - 4, 13, '#efe8da', '#fffaf0', '#c8c0b0');
  // Door with spring couplets and a 福 diamond.
  const doorCol = ['#8a4a2a', '#3a6a5a', '#6a3a2a'][v % 3] ?? '#8a4a2a';
  block(c, cx - 7, by - 21, 14, 21, cafe ? '#5a3a24' : doorCol, shade(doorCol, 0.2));
  rect(c, 'rgba(0,0,0,0.3)', cx - 0.4, by - 20, 0.8, 20);
  for (const dx of [-11, 8]) {
    block(c, cx + dx, by - 21, 3, 17, '#c0392b');
    for (let k = 0; k < 4; k++) rect(c, '#e8c860', cx + dx + 1, by - 19 + k * 4, 1, 1.4);
  }
  c.save();
  c.translate(cx, by - 25);
  c.rotate(Math.PI / 4);
  rect(c, '#c0392b', -2.6, -2.6, 5.2, 5.2);
  c.restore();
  txt(c, '福', cx, by - 25, 3.6, '#ffd34e', 900);
  // Windows with painted wooden frames.
  for (const wx of [L + 9, R - 25]) {
    block(c, wx, by - 21, 16, 11, cafe ? '#5a3a24' : ['#3a6a8a', '#4a7a4a', '#8a5a3a'][v % 3] ?? '#3a6a8a');
    const lit = night || cafe;
    vgrad(c, wx + 1.2, by - 19.8, 13.6, 8.6, lit ? '#ffe2a0' : '#3a4650', lit ? '#e8a860' : '#222a30');
    rect(c, cafe ? '#5a3a24' : '#d8d4cc', wx + 7.6, by - 20, 0.8, 9);
    if (!cafe) for (let k = 1; k < 4; k++) rect(c, '#2a2a2e', wx + k * 4, by - 20, 0.5, 9);
  }
  // Dark tiled roof with a ridge.
  poly(c, '#3a3a40', [L - 3, by - 26, R + 3, by - 26, R - 4, by - 46, L + 4, by - 46]);
  c.save();
  c.beginPath();
  c.moveTo(L - 2, by - 27);
  c.lineTo(R + 2, by - 27);
  c.lineTo(R - 4, by - 45);
  c.lineTo(L + 4, by - 45);
  c.closePath();
  c.clip();
  vgrad(c, L - 3, by - 46, w + 6, 20, '#5a5a62', '#44444c');
  for (let yy = by - 44; yy < by - 26; yy += 3) rect(c, 'rgba(0,0,0,0.25)', L - 3, yy, w + 6, 0.6);
  for (let xx = L - 2; xx < R + 2; xx += 4) rect(c, 'rgba(255,255,255,0.06)', xx, by - 46, 1.4, 20);
  c.restore();
  block(c, L + 2, by - 48, w - 4, 3.5, '#2e2e34', '#44444c');
  rect(c, '#2a2a2e', L - 3, by - 27, w + 6, 1.4);
  // Little extras.
  if (v === 0) {
    for (let i = 0; i < 3; i++) {
      block(c, R - 8 - i * 6, by - 5, 5, 5, '#b8603a', '#d8805a');
      ellipse(c, '#3f8a3a', R - 5.5 - i * 6, by - 6.5, 3, 2.4);
    }
  } else if (v === 1) {
    block(c, L + 4, by - 11, 6, 8, '#2f7a3a', '#3f9a4a');
    rect(c, '#ffffff', L + 5, by - 9, 4, 1);
  } else if (v === 2) {
    for (const dx of [-26, -20]) {
      ellipse(c, '#e2493b', cx + dx, by - 4, 2.6, 1);
      rect(c, '#b83a2e', cx + dx - 2, by - 4, 0.8, 4);
      rect(c, '#b83a2e', cx + dx + 1.4, by - 4, 0.8, 4);
    }
  }
  if (cafe) {
    line(c, '#3a2a20', 0.5, [cx + 14, by - 28, cx + 14, by - 31, cx + 34, by - 31, cx + 34, by - 28]);
    block(c, cx + 13, by - 30, 22, 9, '#f4ead4', '#fffaf0', '#c8b898');
    txt(c, '好坡', cx + 24, by - 26.8, 4.6, '#5a3a24', 900);
    txt(c, 'GOOD SLOPE', cx + 24, by - 23.2, 1.9, '#8a6a4a', 800);
    block(c, L + 3, by - 12, 7, 12, '#2a2a2a', '#3a3a3a');
    for (let k = 0; k < 3; k++) rect(c, '#f4f1ea', L + 4.5, by - 10 + k * 3, 4, 0.5);
  }
}

// ---------------------------------------------------------------------------
// Street life
// ---------------------------------------------------------------------------

function taxi(c: Ctx, x: number, y: number, left: boolean): void {
  shadow(c, x, y, 17, 3.2, 0.32);
  const dir = left ? -1 : 1;
  block(c, x - 16, y - 11, 32, 9, '#f2c230', '#ffe070', '#c09010');
  block(c, x - 9 - dir * 1, y - 18, 18, 8, '#f2c230', '#ffe070');
  vgrad(c, x - 7.5 - dir, y - 16.6, 7, 5.4, '#bfe6f6', '#7ab0c8');
  vgrad(c, x + 0.5 - dir, y - 16.6, 7, 5.4, '#bfe6f6', '#7ab0c8');
  block(c, x - 4, y - 21, 8, 3.4, '#ffffff', '#ffffff', '#c8cdd2');
  txt(c, 'TAXI', x, y - 19.3, 2.4, '#c0392b', 900);
  rect(c, '#2a2a30', x - 16, y - 6, 32, 1);
  ellipse(c, '#1b1d24', x - 9, y - 2, 3.2, 3);
  ellipse(c, '#1b1d24', x + 9, y - 2, 3.2, 3);
  ellipse(c, '#9aa0a6', x - 9, y - 2, 1.2, 1.2);
  ellipse(c, '#9aa0a6', x + 9, y - 2, 1.2, 1.2);
  ellipse(c, '#fff6c0', x + dir * 15, y - 8.5, 1.4, 1.2);
  rect(c, '#e2493b', x - dir * 16 - (left ? 0 : 1), y - 9.5, 1, 2);
  rect(c, '#2a2a30', x - 3, y - 9, 6, 0.6);
}

const SCOOTER_COLS = ['#f4f1ea', '#3a8fd9', '#e2493b', '#2a2a30', '#9fd3c0', '#f2c230', '#c8cdd2', '#ff9fc0', '#57c278'];

/** A row of parked scooters seen from the front, squeezed together on the pavement. */
function scooterRow(c: Ctx, x: number, y: number, w: number, v: number): void {
  const n = Math.max(1, Math.floor(w / 11));
  const step = w / n;
  for (let i = 0; i < n; i++) {
    const sx = x - w / 2 + step * (i + 0.5);
    const col = SCOOTER_COLS[Math.floor(hash(i, v, 13) * SCOOTER_COLS.length)];
    const dy = (i % 2) * 1.5;
    ellipse(c, 'rgba(20,10,6,0.25)', sx, y + dy, 5, 1.6);
    block(c, sx - 3.5, y - 16 + dy, 7, 4, '#2a2a30', '#3a3a40');
    block(c, sx - 4, y - 13 + dy, 8, 10, col, shade(col, 0.25), shade(col, -0.25));
    rect(c, '#1b1d24', sx - 1.2, y - 4 + dy, 2.4, 4);
    ellipse(c, '#fff6c0', sx, y - 10.5 + dy, 1.5, 1);
    rect(c, '#2a2a30', sx - 5.5, y - 14.5 + dy, 11, 1);
    rect(c, '#9aa6ae', sx - 6, y - 16.5 + dy, 1.4, 1.2);
    rect(c, '#9aa6ae', sx + 4.6, y - 16.5 + dy, 1.4, 1.2);
    if (hash(i, v, 14) < 0.35) {
      const hc = SCOOTER_COLS[Math.floor(hash(i, v, 15) * SCOOTER_COLS.length)];
      ellipse(c, hc, sx + 3, y - 17.5 + dy, 2.6, 2.2);
      ellipse(c, 'rgba(255,255,255,0.4)', sx + 2.2, y - 18.5 + dy, 1, 0.6);
    }
  }
}

/** Scooters with riders waiting at the lights: the famous scooter wave. */
function scooterWave(c: Ctx, x: number, y: number, w: number, v: number): void {
  const dir = v % 2 ? -1 : 1;
  for (let row = 0; row < 3; row++) {
    for (let i = 0; i < 3; i++) {
      const sx = x - w / 2 + 10 + i * ((w - 20) / 2) + (row % 2) * 6;
      const sy = y - (2 - row) * 9;
      const k = row * 3 + i + v * 7;
      const body = SCOOTER_COLS[Math.floor(hash(k, 1, 17) * SCOOTER_COLS.length)];
      const coat = ['#3a8fd9', '#e2493b', '#57c278', '#2a2a30', '#f2c94c', '#8a5aa8', '#f4f1ea'][Math.floor(hash(k, 2, 17) * 7)];
      const helmet = SCOOTER_COLS[Math.floor(hash(k, 3, 17) * SCOOTER_COLS.length)];
      ellipse(c, 'rgba(20,10,6,0.25)', sx, sy, 9, 1.8);
      ellipse(c, '#1b1d24', sx - 6, sy - 2, 2.4, 2.4);
      ellipse(c, '#1b1d24', sx + 6, sy - 2, 2.4, 2.4);
      block(c, sx - 7, sy - 7, 12, 4.5, body, shade(body, 0.25));
      block(c, sx + dir * 4 - 1.5, sy - 12, 3.5, 9, body, shade(body, 0.25));
      rect(c, '#2a2a30', sx + dir * 4 - 2, sy - 13, 5, 1);
      // Rider: legs, jacket, arms on the handlebar, helmet with a visor.
      rect(c, '#2f3442', sx - 2.5, sy - 8, 5, 3);
      block(c, sx - 3, sy - 15, 6, 8, coat, shade(coat, 0.2));
      line(c, coat, 1.4, [sx + dir * 1, sy - 12, sx + dir * 4.5, sy - 12.5]);
      ellipse(c, helmet, sx, sy - 17.5, 3, 3);
      rect(c, '#1b1d24', sx + dir * 0.5 - (dir < 0 ? 2.5 : 0), sy - 18, 2.5, 1.6);
      ellipse(c, 'rgba(255,255,255,0.4)', sx - dir * 1, sy - 19, 1, 0.6);
    }
  }
}

function bus(c: Ctx, x: number, y: number, night: boolean): void {
  shadow(c, x, y, 32, 3.6, 0.32);
  block(c, x - 32, y - 24, 64, 21, '#f4f5f2', '#ffffff', '#c8cdd2');
  rect(c, '#2f9a5a', x - 31, y - 9, 62, 3);
  rect(c, '#1f6f4a', x - 31, y - 6, 62, 1);
  for (let i = 0; i < 6; i++) {
    const wx = x - 22 + i * 9;
    vgrad(c, wx, y - 20, 7.5, 8, night ? '#ffe8b8' : '#7ab0c8', night ? '#e8c080' : '#3a6a80');
    if (hash(i, 21) < 0.6) {
      ellipse(c, '#2a2420', wx + 3.8, y - 15.5, 1.4, 1.5);
      rect(c, ['#3a8fd9', '#e2493b', '#f2c94c'][i % 3], wx + 2.4, y - 14, 2.8, 2);
    }
  }
  vgrad(c, x - 31, y - 22, 7, 13, '#bfe6f6', '#6aa0b8');
  block(c, x - 30, y - 26, 18, 4, '#1b1d24');
  c.font = `700 2.6px ui-monospace, monospace`;
  c.textAlign = 'center';
  c.textBaseline = 'middle';
  c.fillStyle = '#ffb43a';
  c.fillText('307 台北車站', x - 21, y - 24);
  for (const wx of [x - 20, x + 20]) {
    ellipse(c, '#1b1d24', wx, y - 2.5, 3.6, 3.4);
    ellipse(c, '#9aa0a6', wx, y - 2.5, 1.4, 1.4);
  }
  ellipse(c, '#fff6c0', x - 31, y - 7, 1.2, 1.2);
}

function guard(c: Ctx, x: number, y: number): void {
  shadow(c, x, y, 4, 1.4);
  rect(c, '#1b1d24', x - 2.4, y - 2, 2, 2);
  rect(c, '#1b1d24', x + 0.4, y - 2, 2, 2);
  block(c, x - 2.6, y - 9, 5.2, 7, '#1f3a2a', '#2f4a3a');
  block(c, x - 3.2, y - 17, 6.4, 9, '#1f3a2a', '#2f5a42');
  rect(c, '#e8c860', x - 0.3, y - 15.5, 0.6, 0.6);
  rect(c, '#e8c860', x - 0.3, y - 13, 0.6, 0.6);
  rect(c, '#e8c860', x - 3.2, y - 17, 6.4, 0.8);
  ellipse(c, '#f0c8a0', x, y - 19.5, 2.2, 2.3);
  ellipse(c, '#ffffff', x, y - 21.5, 2.8, 2.2);
  rect(c, '#ffffff', x - 3.2, y - 21, 6.4, 0.8);
  rect(c, '#2a1810', x - 0.9, y - 19.7, 0.5, 0.5);
  rect(c, '#2a1810', x + 0.5, y - 19.7, 0.5, 0.5);
  // Rifle with its bayonet, held upright.
  rect(c, '#6a4a2a', x + 3.4, y - 22, 1.2, 14);
  rect(c, '#c8cdd2', x + 3.7, y - 26, 0.6, 4);
  ellipse(c, '#ffffff', x + 3.8, y - 12, 1, 1);
}

function bench(c: Ctx, x: number, y: number): void {
  shadow(c, x, y, 13, 2.2);
  for (const lx of [x - 10, x + 8]) rect(c, '#3a3e44', lx, y - 7, 2, 7);
  block(c, x - 12, y - 16, 24, 6, '#a0703e', '#c09060', '#7a5028');
  rect(c, 'rgba(0,0,0,0.2)', x - 11, y - 13, 22, 0.5);
  block(c, x - 12, y - 9, 24, 3.5, '#a0703e', '#c8986a', '#7a5028');
}

function vending(c: Ctx, x: number, y: number, night: boolean): void {
  shadow(c, x, y, 14, 2.6);
  [['#e2493b', -7], ['#2f6fb0', 7]].forEach(([col, dx]) => {
    const vx = x + (dx as number);
    block(c, vx - 6.5, y - 27, 13, 27, col as string, shade(col as string, 0.25), shade(col as string, -0.3));
    vgrad(c, vx - 5, y - 25, 10, 12, night ? '#ffffff' : '#e8f4f8', night ? '#d8ecf4' : '#b8d4e0');
    for (let r = 0; r < 3; r++) for (let k = 0; k < 4; k++) rect(c, ['#e2493b', '#57c278', '#f2c94c', '#3a8fd9', '#ff9fc0'][(r * 4 + k + Math.abs(dx as number)) % 5], vx - 4.4 + k * 2.4, y - 24.5 + r * 4, 1.6, 3);
    block(c, vx - 5, y - 9, 10, 4, '#1b1d24');
    rect(c, '#ffd34e', vx + 2, y - 12, 2, 1.4);
  });
  if (night) ellipse(c, 'rgba(220,240,255,0.25)', x, y - 18, 16, 10);
}

function banyan(c: Ctx, x: number, y: number, t: number): void {
  shadow(c, x, y, 26, 5, 0.3, 30);
  // Trunk and the curtain of aerial roots.
  block(c, x - 6, y - 26, 12, 26, '#6a5040', '#8a6a54', '#4a3428');
  for (let i = -18; i <= 18; i += 4) line(c, 'rgba(90,64,48,0.8)', 0.6, [x + i, y - 34, x + i + Math.sin(i + t * 0.5) * 0.6, y - 34 + 14 + (Math.abs(i) % 7) * 2]);
  for (let k = 0; k < 9; k++) {
    const a = (k / 9) * Math.PI * 2;
    const sw = Math.sin(t * 0.8 + k) * 0.6;
    ellipse(c, k % 2 ? '#2f6a32' : '#3a7a3a', x + Math.cos(a) * 22 + sw, y - 46 + Math.sin(a) * 12, 14, 10);
  }
  ellipse(c, '#4a8a42', x - 6, y - 52, 16, 10);
  ellipse(c, '#5a9a4a', x + 8, y - 56, 12, 7);
  ellipse(c, '#6aaa52', x - 2, y - 60, 9, 5);
  // Red cloth tied around the trunk (a blessed tree).
  rect(c, '#c0392b', x - 6, y - 18, 12, 2);
}

function garbageTruck(c: Ctx, x: number, y: number, t: number): void {
  const tm = timeNow();
  if (tm < H(19, 50) || tm >= H(20, 30)) return;
  shadow(c, x, y, 36, 4, 0.35);
  // Cab facing west, then the yellow compactor body.
  block(c, x - 36, y - 22, 16, 19, '#f2c230', '#ffe070', '#c09010');
  vgrad(c, x - 34, y - 20, 9, 7, '#bfe6f6', '#7ab0c8');
  block(c, x - 20, y - 27, 50, 24, '#f2c230', '#ffe070', '#c09010');
  rect(c, '#2f8a3a', x - 20, y - 14, 50, 3);
  txt(c, '垃圾車', x + 2, y - 20, 6, '#2a1810', 900);
  txt(c, 'GARBAGE TRUCK', x + 2, y - 9, 2.4, '#2a1810', 800);
  block(c, x + 30, y - 25, 6, 21, '#c8a020', '#e0c040');
  for (const wx of [x - 28, x + 4, x + 22]) {
    ellipse(c, '#1b1d24', wx, y - 2.5, 3.6, 3.4);
    ellipse(c, '#9aa0a6', wx, y - 2.5, 1.4, 1.4);
  }
  // Flashing light on the roof and the music.
  const on = Math.floor(t * 4) % 2 === 0;
  ellipse(c, on ? '#ffb43a' : '#a86a10', x - 28, y - 23.5, 1.6, 1.2);
  if (on) ellipse(c, 'rgba(255,180,60,0.35)', x - 28, y - 23.5, 6, 4);
  for (let i = 0; i < 3; i++) {
    const k = (t * 0.5 + i / 3) % 1;
    txt(c, i % 2 ? '♪' : '♫', x - 12 + i * 12 + Math.sin(t * 2 + i) * 3, y - 30 - k * 20, 6, `rgba(255,255,255,${1 - k})`, 900, `rgba(40,30,20,${0.6 * (1 - k)})`);
  }
}

function cafeTables(c: Ctx, x: number, y: number): void {
  for (const dx of [-14, 12]) {
    const tx = x + dx;
    shadow(c, tx, y, 8, 2);
    rect(c, '#6a4a2a', tx - 0.6, y - 9, 1.2, 9);
    ellipse(c, '#a0703e', tx, y - 9, 6, 2.2);
    ellipse(c, '#c09060', tx, y - 9.5, 5, 1.6);
    block(c, tx - 1.6, y - 12.5, 3, 3, '#f4f1ea');
    for (const sx of [-8, 8]) {
      ellipse(c, '#3a6a5a', tx + sx, y - 4, 2.6, 1.1);
      rect(c, '#2a4a3a', tx + sx - 0.4, y - 4, 0.8, 4);
    }
  }
}

function laundry(c: Ctx, x: number, y: number, w: number, t: number): void {
  for (const dx of [-w / 2, w / 2]) {
    rect(c, '#8a6a3a', x + dx - 0.6, y - 22, 1.2, 22);
    shadow(c, x + dx, y, 2, 0.8);
  }
  rect(c, '#c8a060', x - w / 2 - 2, y - 22, w + 4, 1.2);
  const cols = ['#ffffff', '#7ab0d8', '#ff9fc0', '#f2c94c'];
  for (let i = 0; i < 3; i++) {
    const sw = Math.sin(t * 1.4 + i) * 0.6;
    block(c, x - w / 2 + 1 + i * (w / 3) + sw, y - 21, w / 3 - 1.5, 8, cols[i % cols.length]);
  }
}

function marketSign(c: Ctx, x: number, y: number): void {
  shadow(c, x, y, 9, 1.8);
  line(c, '#6a4a2a', 1.2, [x - 6, y, x - 2, y - 18]);
  line(c, '#6a4a2a', 1.2, [x + 6, y, x + 2, y - 18]);
  block(c, x - 9, y - 22, 18, 16, '#f4ead4', '#fffaf0', '#c8b898');
  txt(c, '簡單', x, y - 18.4, 4.4, '#3a6a3a', 900);
  txt(c, '市集', x, y - 14, 4.4, '#3a6a3a', 900);
  txt(c, 'SIMPLE MARKET', x, y - 10.4, 1.9, '#8a5a3a', 800);
  txt(c, '每週日 SUNDAYS', x, y - 8, 1.9, '#c0392b', 800);
}


function pigeons(c: Ctx, x: number, y: number, t: number, seed: number): void {
  // A few pigeons pecking at crumbs on the plaza.
  for (let i = 0; i < 4; i++) {
    const px = x + (hash(i, seed, 41) - 0.5) * 30 + Math.sin(t * 0.3 + i) * 2;
    const py = y + (hash(i, seed, 42) - 0.5) * 10;
    const dir = hash(i, seed, 43) < 0.5 ? -1 : 1;
    const peck = Math.max(0, Math.sin(t * 3 + i * 1.7)) > 0.7 ? 1.2 : 0;
    ellipse(c, 'rgba(20,10,6,0.2)', px, py, 2.6, 0.8);
    ellipse(c, '#8a8e96', px, py - 2, 2.6, 1.7);
    ellipse(c, '#6a6e78', px - dir * 1.2, py - 2.3, 1.4, 1.1);
    ellipse(c, '#5a6070', px + dir * 2.2, py - 3.4 + peck, 1.1, 1);
    rect(c, '#4aa07a', px + dir * 1.4, py - 3, 0.9, 0.6);
    rect(c, '#e8a060', px + dir * 3.2, py - 3.3 + peck, 0.7, 0.4);
    rect(c, '#c86a4a', px - 0.6, py - 0.5, 0.5, 0.6);
    rect(c, '#c86a4a', px + 0.4, py - 0.5, 0.5, 0.6);
  }
}

function infoBooth(c: Ctx, x: number, y: number, night: boolean): void {
  shadow(c, x, y, 16, 3);
  block(c, x - 14, y - 24, 28, 24, '#f4f1ea', '#ffffff', '#c8c0b0');
  vgrad(c, x - 11, y - 20, 22, 10, night ? '#ffe8b0' : '#9fd3f0', night ? '#e8c080' : '#5a9ab8');
  block(c, x - 16, y - 30, 32, 7, '#2f8a5a', '#4faa7a', '#1f6a42');
  txt(c, '旅遊服務中心', x, y - 26.4, 3.6, '#ffffff', 900);
  ellipse(c, '#2f8a5a', x, y - 6, 3.4, 3.4);
  txt(c, 'i', x, y - 5.8, 5, '#ffffff', 900);
  txt(c, 'TOURIST INFO', x, y - 1.6, 2, '#2f6a4a', 800);
}

function bicycle(c: Ctx, x: number, y: number, v: number): void {
  const col = ['#3a8fd9', '#e2493b', '#57c278'][v % 3];
  shadow(c, x, y, 9, 1.6);
  c.strokeStyle = '#2a2a30';
  c.lineWidth = 0.9;
  for (const dx of [-6, 6]) {
    c.beginPath();
    c.arc(x + dx, y - 4, 3.6, 0, Math.PI * 2);
    c.stroke();
  }
  line(c, col, 1.2, [x - 6, y - 4, x - 1, y - 9, x + 4, y - 9, x + 6, y - 4]);
  line(c, col, 1.2, [x - 1, y - 9, x + 1, y - 4, x - 6, y - 4]);
  rect(c, '#2a2a30', x - 2.6, y - 11, 3.4, 1.2);
  line(c, '#55606a', 0.8, [x + 4, y - 9, x + 4.6, y - 12, x + 6.4, y - 12]);
  rect(c, '#c8a060', x + 5, y - 13, 4, 3);
}

function publicArt(c: Ctx, x: number, y: number, t: number): void {
  // A big looping ribbon sculpture: Xinyi is full of public art.
  shadow(c, x, y, 16, 3.2, 0.3, 30);
  block(c, x - 12, y - 6, 24, 6, '#5a5f64', '#7a8088', '#3a3e44');
  txt(c, '公共藝術', x, y - 2.6, 2.6, '#e8ecef', 800);
  c.lineCap = 'round';
  c.strokeStyle = '#a8261c';
  c.lineWidth = 6;
  c.beginPath();
  c.ellipse(x, y - 22, 9, 14, 0.5, 0, Math.PI * 2);
  c.stroke();
  c.strokeStyle = '#e2493b';
  c.lineWidth = 4;
  c.stroke();
  c.strokeStyle = '#ffb43a';
  c.lineWidth = 3.4;
  c.beginPath();
  c.ellipse(x + 2, y - 20, 12, 7, -0.6, 0, Math.PI * 2);
  c.stroke();
  c.lineCap = 'butt';
  ellipse(c, `rgba(255,255,255,${0.25 + Math.sin(t) * 0.1})`, x - 5, y - 32, 2, 1);
}

// ---------------------------------------------------------------------------
// Skybridges (信義空橋)
// ---------------------------------------------------------------------------

const E = 36; // deck height above the ground
function skyNS(c: Ctx, x: number, y: number, end: boolean): void {
  // One tile row of a north–south bridge, 2 tiles wide, drawn E px above its ground row. Its shadow on the
  // ground peeks out to one side, which is what shows the deck is up in the air.
  const L = x - T, y0 = y - T;
  const off = 5 + sun.dx * 9;
  rect(c, 'rgba(20,10,6,0.18)', L + off, y0, 2 * T, T);
  const d0 = y0 - E;
  rect(c, '#55606a', L - 1, d0, 2 * T + 2, T);
  rect(c, '#d4d8dc', L + 3, d0, 2 * T - 6, T);
  rect(c, 'rgba(0,0,0,0.07)', L + 3, d0 + 7.5, 2 * T - 6, 0.6);
  rect(c, '#e8c860', x - 0.4, d0, 0.8, T);
  // Glass parapets on both edges, with the steel handrail and posts.
  for (const ex of [L, x + T - 3]) {
    rect(c, 'rgba(150,210,230,0.6)', ex, d0 - 7, 3, T);
    rect(c, '#3a424a', ex + 1, d0 - 8, 1, T);
    rect(c, '#3a424a', ex, d0 - 8 + ((y / T) % 2 ? 0 : 8), 3, 1);
  }
  // Translucent arched roof seen from above.
  rect(c, 'rgba(210,240,250,0.25)', L + 1, d0 - 11, 2 * T - 2, T);
  rect(c, 'rgba(255,255,255,0.4)', L + 7, d0 - 11, 1, T);
  rect(c, '#6a7480', L + 1, d0 - 11 + ((y / T) % 2 ? 0 : 8), 2 * T - 2, 0.6);
  if (end) {
    // Where it plugs into the 101 podium on the second floor.
    block(c, L - 3, d0 - 14, 2 * T + 6, 6, '#e8ecef', '#ffffff', '#a8b0b8');
    block(c, x - 13, d0 - 9, 26, 6, '#1f6fc0', '#3a8fe0', '#14508e');
    txt(c, '信義空橋 2F', x, d0 - 6, 3.4, '#ffffff', 900);
  }
}

function skyEW(c: Ctx, x: number, y: number, w: number, pillar: boolean, night: boolean): void {
  // A 2-tile-deep east–west span: ground rows y-32..y, deck E px higher, south face visible.
  const L = x - w / 2, gy0 = y - 2 * T;
  rect(c, 'rgba(20,10,6,0.14)', L, gy0 + 2, w, 2 * T - 2);
  if (pillar) {
    ellipse(c, 'rgba(20,10,6,0.25)', x, y - 1, 5, 1.6);
    block(c, x - 3, y - E + 6, 6, E - 6, '#b8bcc0', '#d8dcdf', '#8a9096');
  }
  const top = gy0 - E, bot = y - E;
  // North parapet (far side), walkway, south parapet and the fascia facing us.
  rect(c, 'rgba(150,210,230,0.5)', L, top - 8, w, 8);
  rect(c, '#55606a', L, top - 9, w, 1);
  rect(c, '#d4d8dc', L, top, w, bot - top);
  for (let k = 4; k < bot - top; k += 8) rect(c, 'rgba(0,0,0,0.06)', L, top + k, w, 0.6);
  rect(c, 'rgba(150,210,230,0.6)', L, bot - 9, w, 9);
  for (let px = L; px < L + w; px += 8) rect(c, '#6a7480', px, bot - 9, 0.6, 9);
  rect(c, '#55606a', L, bot - 10, w, 1.2);
  block(c, L, bot, w, 6, '#e8ecef', '#ffffff', '#a8b0b8');
  if (night) rect(c, 'rgba(160,220,255,0.8)', L, bot + 4, w, 0.8);
  if (pillar) {
    block(c, x - 12, bot - 1, 24, 6, '#1f6fc0', '#3a8fe0', '#14508e');
    txt(c, '信義空橋', x, bot + 2, 3.6, '#ffffff', 900);
  }
}

function skyStairs(c: Ctx, x: number, y: number, night: boolean): void {
  // Glass stair-and-lift tower at the west end of the skybridge.
  shadow(c, x, y, 18, 3);
  block(c, x - 16, y - E - 18, 32, E + 18, '#55606a', '#6a7480', '#3a424a');
  vgrad(c, x - 14, y - E - 16, 28, E + 14, night ? '#d8f0ff' : '#9fd0e4', night ? '#a8d0e8' : '#5a90a8');
  for (let k = 0; k < 7; k++) line(c, 'rgba(40,60,70,0.6)', 1, [x - 12 + k * 4, y - 3 - k * 7, x - 8 + k * 4, y - 3 - k * 7]);
  for (let px = x - 14; px < x + 14; px += 7) rect(c, '#55606a', px, y - E - 16, 0.6, E + 14);
  block(c, x - 18, y - E - 22, 36, 5, '#e8ecef', '#ffffff', '#a8b0b8');
  block(c, x - 12, y - 14, 24, 7, '#1f6fc0', '#3a8fe0');
  txt(c, '空橋', x - 5, y - 10.5, 4.4, '#ffffff', 900);
  txt(c, 'SKYBRIDGE', x + 6, y - 10.5, 1.9, '#cfe6f2', 800);
}

// ---------------------------------------------------------------------------
// Elephant Mountain
// ---------------------------------------------------------------------------

const ROCKS = [
  [30, 26, -0.1],
  [22, 20, 0.2],
  [28, 30, 0],
  [18, 26, 0.3],
  [20, 16, -0.2],
  [24, 18, 0.1],
] as const;

function rock(c: Ctx, x: number, y: number, v: number): void {
  const [w, h, tilt] = ROCKS[v % ROCKS.length];
  shadow(c, x, y, w / 2 + 2, 3.2, 0.35, h);
  c.fillStyle = '#6e6a60';
  c.beginPath();
  c.moveTo(x - w / 2, y);
  c.quadraticCurveTo(x - w / 2 - 2, y - h * 0.7, x - w * 0.2 + tilt * 10, y - h);
  c.quadraticCurveTo(x + w * 0.3 + tilt * 10, y - h - 2, x + w / 2, y - h * 0.45);
  c.quadraticCurveTo(x + w / 2 + 1, y - 2, x + w / 2 - 2, y);
  c.closePath();
  c.fill();
  c.fillStyle = '#9a958a';
  c.beginPath();
  c.moveTo(x - w / 2 + 2, y - 2);
  c.quadraticCurveTo(x - w / 2, y - h * 0.68, x - w * 0.2 + tilt * 10, y - h + 1.5);
  c.quadraticCurveTo(x + w * 0.1 + tilt * 10, y - h * 0.8, x - w * 0.05, y - 2);
  c.closePath();
  c.fill();
  // Weathered sandstone layers and moss.
  for (let k = 1; k < 4; k++) line(c, 'rgba(60,56,48,0.35)', 0.5, [x - w / 2 + 2, y - (h * k) / 4, x + w / 2 - 2, y - (h * k) / 4 - tilt * 6]);
  ellipse(c, '#5f8a4a', x + w * 0.15, y - h + 3, w * 0.22, 2);
  ellipse(c, '#4f7a3a', x - w * 0.3, y - 2, w * 0.18, 1.6);
}

function railing(c: Ctx, x: number, y: number, h: number): void {
  // A wooden rail running north–south along x, from y - h to y.
  rect(c, '#7a5232', x - 0.8, y - h - 8, 1.6, h);
  rect(c, '#a0703e', x - 0.4, y - h - 8, 0.6, h);
  for (let yy = y - h; yy <= y; yy += 12) {
    rect(c, '#5a3a24', x - 1.2, yy - 9, 2.4, 9);
    shadow(c, x, yy, 2, 0.8);
  }
}

function stepLamp(c: Ctx, x: number, y: number, night: boolean): void {
  shadow(c, x, y, 3, 1);
  block(c, x - 2, y - 9, 4, 9, '#5a5a52', '#7a7a70');
  rect(c, night ? '#fff0b0' : '#d8d4c4', x - 1.4, y - 7.6, 2.8, 2);
  if (night) ellipse(c, 'rgba(255,230,160,0.3)', x, y - 4, 7, 4);
}

// ---------------------------------------------------------------------------
// MRT station extras
// ---------------------------------------------------------------------------

function metroLogo(c: Ctx, cx: number, cy: number, r: number): void {
  ellipse(c, '#ffffff', cx, cy, r, r);
  ellipse(c, '#1f6fc0', cx, cy, r * 0.78, r * 0.78);
  rect(c, '#ffffff', cx - r * 0.5, cy - r * 0.12, r, r * 0.24);
  rect(c, '#ffffff', cx - r * 0.12, cy - r * 0.5, r * 0.24, r);
}

function lineStrip(c: Ctx, x: number, y: number, w: number): void {
  const L = x - w / 2, top = y - 15;
  block(c, L, top, w, 12, '#f4f6f7', '#ffffff', '#c8cdd2');
  rect(c, '#e3002c', L + 3, top + 5, w - 6, 1.6);
  const names = ['淡水', '士林', '中山', '台北車站', '台大醫院', '中正紀念堂', '大安', '101', '象山'];
  names.forEach((n, i) => {
    const sx = L + 4 + (i * (w - 8)) / (names.length - 1);
    const here = i === 3;
    ellipse(c, '#ffffff', sx, top + 5.8, here ? 1.8 : 1.2, here ? 1.8 : 1.2);
    ellipse(c, here ? '#e3002c' : '#2a2a2a', sx, top + 5.8, here ? 1.1 : 0.6, here ? 1.1 : 0.6);
    txt(c, n, sx, top + 9.6, n.length > 3 ? 1.5 : 1.8, here ? '#e3002c' : '#2a2a2a', 800);
  });
  txt(c, '淡水信義線 Tamsui–Xinyi Line', L + w / 2, top + 2.4, 2, '#e3002c', 800);
}

function adWall(c: Ctx, x: number, y: number, w: number): void {
  const L = x - w / 2, top = y - 15;
  block(c, L, top, w, 12, '#c8cdd2', '#e8ecef', '#8a9096');
  vgrad(c, L + 1.5, top + 1.5, w - 3, 9, '#ffcf80', '#ff8a5a');
  // A travel poster: Elephant Mountain at sunset with Taipei 101.
  poly(c, '#3d6a3a', [L + 2, top + 10.5, L + 14, top + 4, L + 26, top + 10.5]);
  for (let i = 0; i < 5; i++) poly(c, '#2a4a5a', [L + 30 + i * 2, top + 10.5 - i * 1.2, L + 35 - i * 0.4, top + 10.5 - i * 1.2, L + 35 - i * 0.6, top + 10.5 - (i + 1) * 1.2, L + 30 + i * 2.2, top + 10.5 - (i + 1) * 1.2]);
  rect(c, '#2a4a5a', L + 32.6, top + 2.5, 0.6, 2.4);
  txt(c, '象山步道', L + w * 0.72, top + 4.6, 4, '#ffffff', 900, 'rgba(120,40,0,0.5)');
  txt(c, 'Hike Elephant Mountain!', L + w * 0.72, top + 8.4, 2, '#5a2a10', 800);
}

function exitBoard(c: Ctx, x: number, y: number): void {
  const top = y - 30;
  block(c, x - 13, top, 26, 10, '#f2c230', '#ffe070', '#c09010');
  poly(c, '#2a2a2a', [x - 11, top + 5, x - 8, top + 2, x - 8, top + 3.8, x - 5, top + 3.8, x - 5, top + 6.2, x - 8, top + 6.2, x - 8, top + 8]);
  txt(c, '出口', x + 2, top + 3.6, 4.2, '#2a2a2a', 900);
  txt(c, 'EXIT · 台北車站', x + 2, top + 7.6, 1.9, '#2a2a2a', 800);
}

function queueLines(c: Ctx, x: number, y: number, w: number): void {
  // Same markings as the mrtRide platform: yellow standing lines beside each door and a green arrow.
  for (let dx = 40; dx < x + w; dx += 48) {
    if (dx < x) continue;
    const yy = y + 7;
    rect(c, 'rgba(255,211,78,0.8)', dx - 11, yy, 4, 7);
    rect(c, 'rgba(255,211,78,0.8)', dx + 7, yy, 4, 7);
    poly(c, 'rgba(60,180,100,0.85)', [dx - 2, yy, dx + 2, yy, dx + 2, yy + 4, dx + 4, yy + 4, dx, yy + 8, dx - 4, yy + 4, dx - 2, yy + 4]);
  }
}

function mrtName(c: Ctx, x: number, y: number, v: number): void {
  const top = y - 46;
  line(c, '#8a9096', 0.5, [x - 14, top - 12, x - 14, top]);
  line(c, '#8a9096', 0.5, [x + 14, top - 12, x + 14, top]);
  block(c, x - 20, top, 40, 11, '#1b2a36', '#2a3a46', '#0e1820');
  if (v === 0) {
    rect(c, '#e3002c', x - 19, top + 1, 2.4, 9);
    rect(c, '#0070bd', x - 16.2, top + 1, 2.4, 9);
    txt(c, '台北車站', x + 2, top + 4, 5, '#ffffff', 900);
    txt(c, 'Taipei Main Station', x + 2, top + 8.4, 2.2, '#cfe6f2', 700);
  } else {
    txt(c, '往 象山 →', x, top + 4, 4.2, '#ffffff', 900);
    txt(c, 'To Xiangshan · Taipei 101', x, top + 8.4, 2, '#ffd34e', 700);
  }
}

function pillarAd(c: Ctx, x: number, y: number, v: number): void {
  shadow(c, x, y, 7, 2);
  block(c, x - 6, y - 70, 12, 70, '#dfe4e8', '#f4f6f7', '#a8b0b8');
  block(c, x - 5, y - 40, 10, 22, '#2a2e34');
  if (v) {
    vgrad(c, x - 4.2, y - 39.2, 8.4, 20.4, '#8fd0f0', '#2a7ac0');
    txt(c, '花蓮', x, y - 34, 3.2, '#ffffff', 900);
    poly(c, '#3f7a4a', [x - 4.2, y - 22, x - 1, y - 30, x + 2, y - 24, x + 4.2, y - 27, x + 4.2, y - 19, x - 4.2, y - 19]);
  } else {
    vgrad(c, x - 4.2, y - 39.2, 8.4, 20.4, '#ffe0a8', '#ffb07a');
    block(c, x - 2, y - 34, 4, 9, 'rgba(255,250,240,0.95)');
    rect(c, '#c49a6c', x - 1.6, y - 30, 3.2, 4.6);
    txt(c, '珍奶', x, y - 21.5, 2.6, '#7a2a10', 900);
  }
  rect(c, '#2f8fa8', x - 6, y - 8, 12, 1.4);
}

// ---------------------------------------------------------------------------
// Object drawers
// ---------------------------------------------------------------------------

type Drawer = (c: Ctx, r: Rect, t: number) => void;

function mrtExit(c: Ctx, r: Rect, name: string, en: string): void {
  const { x, w } = r;
  const by = r.y + r.h, cx = x + w / 2;
  shadow(c, cx, by, w / 2 + 3, 3);
  // A glass box over the escalator down, with a slab roof and the line-coloured name band.
  block(c, x - 2, by - 30, w + 4, 30, '#55606a', '#6a7480', '#3a424a');
  vgrad(c, x, by - 28, w, 28, isNight() ? '#e0f2ff' : '#a8d8e8', isNight() ? '#a8d0e8' : '#5a90a8');
  for (let k = 0; k < 5; k++) line(c, 'rgba(40,60,70,0.55)', 1, [x + 6 + k * 5, by - 2 - k * 4, x + 10 + k * 5, by - 2 - k * 4]);
  for (let px = x; px < x + w; px += 8) rect(c, '#55606a', px, by - 28, 0.6, 28);
  block(c, x - 5, by - 36, w + 10, 6, '#e8ecef', '#ffffff', '#a8b0b8');
  block(c, x - 3, by - 44, w + 6, 9, '#1f6fc0', '#3a8fe0', '#14508e');
  metroLogo(c, x + 3, by - 39.5, 3.4);
  txt(c, name, cx + 3, by - 41.2, 4.4, '#ffffff', 900);
  txt(c, en, cx + 3, by - 37.6, 1.9, '#cfe6f2', 700);
  if (isNight()) ellipse(c, 'rgba(120,190,255,0.22)', cx, by - 40, w / 2 + 8, 8);
}

function marketStallDrawer(goods: 'soap' | 'plants' | 'cards'): Drawer {
  return (c, r, t) => {
    const { x, y, w, h } = r;
    shadow(c, x + w / 2, y + h, w / 2 + 2, 3, 0.3);
    for (const px of [x + 1, x + w - 2]) rect(c, '#e8e4dc', px, y - 30, 1.4, 32);
    // White canvas canopy with a gentle sag in the wind.
    const sw = Math.sin(t * 1.3 + x) * 0.5;
    poly(c, '#f4f1ea', [x - 3, y - 30, x + w + 3, y - 30, x + w + 1, y - 38 + sw, x - 1, y - 38 + sw]);
    for (let i = 0; i < w + 4; i += 8) rect(c, 'rgba(0,0,0,0.06)', x - 2 + i, y - 37, 3, 7);
    block(c, x, y - 9, w, h + 9, '#c8a878', '#e0c494', '#9a7a50');
    rect(c, '#f4f1ea', x + 1, y - 10, w - 2, 1.5);
    if (goods === 'soap') {
      for (let i = 0; i < 6; i++) block(c, x + 3 + i * 7, y - 14, 5, 4, ['#ffd0a0', '#c8e8a0', '#ffb0c0', '#f4f1ea'][i % 4]);
      for (let i = 0; i < 2; i++) {
        ellipse(c, '#b08a50', x + w - 10 + i * 6, y - 13, 3, 2);
        line(c, '#8a6a3a', 0.4, [x + w - 13 + i * 6, y - 13, x + w - 7 + i * 6, y - 13]);
      }
    } else if (goods === 'plants') {
      for (let i = 0; i < 5; i++) {
        block(c, x + 4 + i * 8, y - 13, 5, 4, ['#f4f1ea', '#9ad0e8', '#ff9fc0'][i % 3]);
        ellipse(c, ['#5f9a4a', '#7aba5a', '#4f8a3a'][i % 3], x + 6.5 + i * 8, y - 15, 3, 2.4);
      }
    } else {
      for (let i = 0; i < 8; i++) {
        block(c, x + 3 + (i % 4) * 10, y - 16 + Math.floor(i / 4) * 4.6, 8, 4, '#fbf8f0');
        rect(c, ['#3a8fd9', '#e2493b', '#57c278', '#f2c94c'][i % 4], x + 4 + (i % 4) * 10, y - 15 + Math.floor(i / 4) * 4.6, 3, 2);
      }
    }
    block(c, x + w / 2 - 12, y - 46, 24, 8, '#5a3a24', '#7a5232');
    txt(c, goods === 'soap' ? '手工皂' : goods === 'plants' ? '小盆栽' : '明信片', x + w / 2, y - 42, 4.4, '#f4ead4', 900);
  };
}

const OBJ: Record<string, Drawer> = {
  xy_taxi(c, r) {
    const { x, y, w, h } = r;
    const cx = x + w / 2, by = y + h;
    shadow(c, cx, by, 4, 1.4);
    rect(c, '#55606a', cx - 0.8, by - 34, 1.6, 34);
    block(c, cx - 9, by - 42, 18, 11, '#f2c230', '#ffe070', '#c09010');
    txt(c, '計程車', cx, by - 38.4, 4.2, '#2a1810', 900);
    txt(c, 'TAXI', cx, by - 34.4, 2.6, '#2a1810', 900);
  },
  xy_cuihu(c, r) {
    const { x, y, w, h } = r;
    const by = y + h, cx = x + w / 2;
    shadow(c, cx, by, w / 2, 2);
    rect(c, '#6a4a2a', cx - 0.8, by - 18, 1.6, 18);
    block(c, cx - 11, by - 26, 22, 10, '#3a6a4a', '#4f8a5a', '#24442e');
    txt(c, '翠湖', cx, by - 22.6, 4.8, '#f4ead4', 900);
    txt(c, 'CUIHU POND', cx, by - 18.6, 2, '#d8e8c8', 800);
  },
  xy_mrtSys: (c, r) => mrtExit(c, r, '國父紀念館', 'S.Y.S. Memorial Hall'),
  xy_mrt101: (c, r) => mrtExit(c, r, '台北101/世貿', 'Taipei 101 / World Trade Center'),
  xy_mall101(c, r) {
    const { x, w } = r;
    const by = r.y + r.h, cx = x + w / 2;
    block(c, x - 3, by - 28, w + 6, 28, '#8a8f94', '#b8bdc2', '#5a5f64');
    vgrad(c, x, by - 23, w, 23, isNight() ? '#ffe8b0' : '#25384a', isNight() ? '#e8b878' : '#121c26');
    for (let i = 0; i <= 3; i++) rect(c, '#c8cdd2', x + i * (w / 3), by - 23, 0.8, 23);
    block(c, x - 7, by - 32, w + 14, 5, '#d8dcdf', '#f4f6f7', '#9aa0a6');
    block(c, cx - 18, by - 42, 36, 10, '#2a2e34', '#3a3e46');
    txt(c, '購物中心', cx - 4, by - 38.6, 5, '#e8c860', 900);
    txt(c, 'MALL', cx + 12, by - 38.6, 3.2, '#ffffff', 900);
    txt(c, 'TAIPEI 101', cx, by - 34.6, 2.2, '#c8cdd2', 700);
  },
  xy_love(c, r, t) {
    const { x, y, w, h } = r;
    const cx = x + w / 2, by = y + h;
    shadow(c, cx, by, w / 2 + 2, 3.5, 0.3, 30);
    block(c, cx - 18, by - 7, 36, 7, '#5a5f64', '#7a8088', '#3a3e44');
    txt(c, 'LOVE · 愛', cx, by - 3.4, 2.6, '#e8ecef', 800);
    // The big red character with a chunky extrusion.
    c.font = `900 34px ${CJK}`;
    c.textAlign = 'center';
    c.textBaseline = 'alphabetic';
    for (let k = 4; k > 0; k--) {
      c.fillStyle = k > 2 ? '#7a1010' : '#a01818';
      c.fillText('愛', cx + k * 0.6, by - 7 + k * 0.2);
    }
    c.fillStyle = '#e2302a';
    c.fillText('愛', cx, by - 7);
    const glint = (Math.sin(t * 0.8) + 1) / 2;
    ellipse(c, `rgba(255,255,255,${0.15 + glint * 0.2})`, cx - 6, by - 30, 3, 1.4);
  },
  xy_fireworks(c, r, t) {
    const { x, w } = r;
    const by = r.y + r.h, cx = x + w / 2;
    shadow(c, cx, by, w / 2 + 2, 2.4);
    for (const px of [x + 2, x + w - 4]) rect(c, '#55606a', px, by - 14, 2, 14);
    block(c, x - 6, by - 40, w + 12, 27, '#1b2a36', '#2a3a46', '#0e1820');
    rect(c, '#140a34', x - 4, by - 38, w + 8, 18);
    // Little 101 with bursts of fireworks.
    for (let i = 0; i < 4; i++) poly(c, '#6fb4ac', [cx - 3, by - 21 - i * 3.6, cx + 3, by - 21 - i * 3.6, cx + 2.2, by - 24 - i * 3.6, cx - 2.2, by - 24 - i * 3.6]);
    rect(c, '#9aa8a6', cx - 0.3, by - 38, 0.6, 3);
    for (let i = 0; i < 3; i++) {
      const k = (t * 0.6 + i / 3) % 1;
      const fx = cx + (i - 1) * 10, fy = by - 32 + (i % 2) * 4, rr = 1 + k * 5;
      for (let a = 0; a < 8; a++) ellipse(c, NEON[(a + i) % NEON.length], fx + Math.cos(a * 0.785) * rr, fy + Math.sin(a * 0.785) * rr, 0.6, 0.6);
    }
    txt(c, '跨年煙火 00:00', cx, by - 17, 3.4, '#ffd34e', 900);
    txt(c, 'NEW YEAR FIREWORKS', cx, by - 14.6, 1.9, '#ffffff', 700);
  },
  xy_trash(c, r) {
    const { x, w } = r;
    const by = r.y + r.h, cx = x + w / 2;
    shadow(c, cx, by, 6, 1.6);
    rect(c, '#55606a', cx - 0.8, by - 30, 1.6, 30);
    block(c, cx - 9, by - 38, 18, 10, '#f2c230', '#ffe070', '#c09010');
    txt(c, '垃圾車', cx, by - 34.8, 4, '#2a1810', 900);
    txt(c, '停靠點 20:00', cx, by - 30.8, 2.4, '#2a1810', 800);
    ellipse(c, '#2a2a30', cx + 7, by - 3, 3, 3);
    ellipse(c, '#3a8fd9', cx - 6, by - 2.5, 2.6, 2.6);
    ellipse(c, 'rgba(255,255,255,0.3)', cx + 6, by - 4.4, 1, 0.6);
  },
  xy_shrine(c, r, t) {
    const { x, w } = r;
    const by = r.y + r.h, cx = x + w / 2;
    shadow(c, cx, by, w / 2 + 2, 3);
    block(c, cx - 18, by - 6, 36, 6, '#b8b0a4', '#d0c8bc', '#8a8274');
    block(c, cx - 14, by - 26, 28, 20, '#c0392b', '#e05a48', '#8a2a1e');
    vgrad(c, cx - 7, by - 22, 14, 14, '#ffd890', '#c87a30');
    ellipse(c, '#f0c8a0', cx, by - 15, 2.2, 2.2);
    block(c, cx - 3, by - 13, 6, 5, '#e8c860', '#fff0a0');
    ellipse(c, '#f4f1ea', cx, by - 17.4, 2.4, 1);
    for (const dx of [-12, 9]) block(c, cx + dx, by - 24, 3, 16, '#f2c94c');
    // Swallowtail roof with curled ridge ends.
    poly(c, '#2f8a5a', [cx - 20, by - 25, cx + 20, by - 25, cx + 15, by - 32, cx - 15, by - 32]);
    rect(c, '#f2a23a', cx - 20, by - 26, 40, 1.4);
    block(c, cx - 15, by - 35, 30, 3.5, '#e2493b', '#ff7a6a');
    for (const dir of [-1, 1]) poly(c, '#e2493b', [cx + dir * 15, by - 32, cx + dir * 21, by - 39, cx + dir * 19, by - 34]);
    txt(c, '福德宮', cx, by - 28.6, 3.4, '#ffd34e', 900);
    // Incense burner and the smoke.
    block(c, cx - 4, by - 8, 8, 6, '#9a7a2a', '#c8a040', '#6a5010');
    for (let i = 0; i < 3; i++) {
      const k = (t * 0.5 + i / 3) % 1;
      ellipse(c, `rgba(240,240,240,${0.5 * (1 - k)})`, cx + Math.sin(t * 1.5 + i * 2) * 2, by - 10 - k * 18, 1 + k * 2, 0.8 + k * 1.4);
    }
    for (let i = 0; i < 3; i++) ellipse(c, ['#ffb43a', '#e2493b', '#f2c94c'][i], cx - 14 + i * 3, by - 7.6, 1.4, 1.4);
  },
  xy_village(c, r) {
    const { x, w } = r;
    const by = r.y + r.h, cx = x + w / 2;
    shadow(c, cx, by, 10, 2);
    for (const dx of [-8, 7]) rect(c, '#5a3a24', cx + dx, by - 18, 1.6, 18);
    block(c, cx - 12, by - 30, 24, 16, '#7a5232', '#9a7050', '#5a3a24');
    rect(c, '#f4ead4', cx - 10.5, by - 28.5, 21, 13);
    txt(c, '四四南村', cx, by - 25.6, 4, '#5a3a24', 900);
    rect(c, '#8e8c86', cx - 8, by - 23, 16, 5);
    rect(c, '#3a3a40', cx - 8, by - 24, 16, 1.5);
    txt(c, '1948', cx, by - 16.8, 2.2, '#8a5a3a', 800);
  },
  xy_market1: marketStallDrawer('soap'),
  xy_market2: marketStallDrawer('plants'),
  xy_market3: marketStallDrawer('cards'),
  xy_stage(c, r, t) {
    const { x, y, w, h } = r;
    const cx = x + w / 2, cy = y + h / 2;
    c.strokeStyle = 'rgba(255,211,78,0.85)';
    c.lineWidth = 1;
    c.setLineDash([3, 2]);
    c.beginPath();
    c.ellipse(cx, cy, w / 2 - 2, h / 2 - 3, 0, 0, Math.PI * 2);
    c.stroke();
    c.setLineDash([]);
    txt(c, '街頭藝人 STREET ARTIST', cx, y + h - 3, 2.6, 'rgba(255,240,180,0.9)', 800);
    // Amplifier and the open hat for tips.
    block(c, x + 2, cy - 6, 7, 8, '#2a2a30', '#3a3a40');
    ellipse(c, '#55606a', x + 5.5, cy - 2, 2, 2);
    ellipse(c, '#3a2a20', x + w - 6, cy + 2, 4, 1.8);
    ellipse(c, '#1b1410', x + w - 6, cy + 1.6, 3, 1.1);
    const k = (t * 0.7) % 1;
    txt(c, '♪', cx + Math.sin(t) * 6, cy - 6 - k * 10, 5, `rgba(255,255,255,${0.8 * (1 - k)})`, 900);
  },
  xy_trailhead(c, r) {
    const { x, w } = r;
    const by = r.y + r.h, cx = x + w / 2;
    shadow(c, cx, by, w / 2 + 4, 2.4);
    for (const dx of [-14, 12]) block(c, cx + dx, by - 30, 3, 30, '#6a4a2a', '#8a6a3a');
    block(c, cx - 17, by - 36, 34, 8, '#5a3a24', '#7a5232', '#3a2416');
    txt(c, '象山步道', cx, by - 32, 5, '#f4ead4', 900);
    block(c, cx - 11, by - 26, 22, 14, '#f4ead4', '#fffaf0', '#c8b898');
    // A tiny map: the trail climbing to the viewpoint, and an elephant.
    line(c, '#c0392b', 0.6, [cx - 8, by - 14, cx - 4, by - 18, cx + 1, by - 16, cx + 6, by - 23]);
    ellipse(c, '#e2493b', cx + 6, by - 23, 0.9, 0.9);
    ellipse(c, '#8a8a90', cx - 6, by - 22, 3, 2);
    rect(c, '#8a8a90', cx - 9.4, by - 22, 1, 3);
    txt(c, '20 min', cx + 4, by - 13.6, 2.2, '#3a6a3a', 800);
  },
  xy_rocks(c, r) {
    const { x, w } = r;
    const by = r.y + r.h, cx = x + w / 2;
    shadow(c, cx, by, 4, 1.4);
    rect(c, '#6a4a2a', cx - 0.8, by - 20, 1.6, 20);
    block(c, cx - 10, by - 28, 20, 9, '#5a3a24', '#7a5232');
    txt(c, '六巨石', cx, by - 24.8, 4, '#f4ead4', 900);
    txt(c, 'SIX GIANT ROCKS', cx, by - 21.2, 1.8, '#d8c8a8', 800);
  },
  xy_viewpoint(c, r) {
    const { x, w } = r;
    const by = r.y + r.h, cx = x + w / 2;
    shadow(c, cx, by, 5, 1.6);
    rect(c, '#55606a', cx - 1, by - 16, 2, 16);
    // Coin-operated binoculars pointing west, at 101.
    block(c, cx - 5, by - 22, 9, 6, '#2f6a8a', '#4a8aaa', '#1f4a6a');
    ellipse(c, '#1b1d24', cx - 5, by - 20.4, 1.6, 1.6);
    ellipse(c, '#1b1d24', cx - 5, by - 17.6, 1.6, 1.6);
    rect(c, '#ffd34e', cx + 1, by - 21, 1.6, 0.8);
  },
  xy_easycard(c, r) {
    const { x, w } = r;
    const by = r.y + r.h, cx = x + w / 2;
    shadow(c, cx, by, w / 2, 2.4);
    block(c, x, 4, w, by - 4, '#e8ecef', '#ffffff', '#a8b0b8');
    rect(c, '#1f6fc0', x + 1, 5, w - 2, 3);
    txt(c, '悠遊卡加值', cx, 6.6, 2.4, '#ffffff', 900);
    vgrad(c, x + 4, 10, w - 8, 9, '#2a6ac0', '#5ab0f0');
    txt(c, 'TOP UP', cx, 13, 2.6, '#ffffff', 900);
    txt(c, 'NT$ 100', cx, 16.6, 2.2, '#ffd34e', 800);
    block(c, x + 5, 21, 9, 5, '#2a2e34');
    ellipse(c, '#57c278', x + 9.5, 23.5, 2.6, 1.6);
    block(c, x + w - 13, 21, 8, 8, '#55606a');
    rect(c, '#1b1d24', x + w - 11, 23, 4, 0.8);
    rect(c, '#1b1d24', x + w - 11, 26, 4, 1.4);
  },
  xy_priority(c, r) {
    const { x, w } = r;
    const by = r.y + r.h, cx = x + w / 2;
    shadow(c, cx, by, w / 2, 2.6);
    for (const lx of [x + 4, x + w - 6]) block(c, lx, by - 7, 2, 7, '#6a7078');
    block(c, x + 1, by - 18, w - 2, 7, '#1f3a7a', '#3a5aa0', '#122458');
    block(c, x, by - 11, w, 4, '#1f3a7a', '#4a6ab0', '#122458');
    for (let i = 1; i < 3; i++) rect(c, '#55606a', x + (i * w) / 3 - 0.6, by - 18, 1.2, 11);
    block(c, cx - 14, by - 32, 28, 12, '#f4f6f7', '#ffffff', '#c8cdd2');
    txt(c, '博愛座', cx, by - 28.4, 4.4, '#1f3a7a', 900);
    txt(c, 'PRIORITY SEAT', cx, by - 24.6, 1.9, '#55606a', 800);
    for (let i = 0; i < 4; i++) ellipse(c, '#1f3a7a', cx - 7.5 + i * 5, by - 21.8, 0.9, 0.9);
  },
};

// ---------------------------------------------------------------------------
// Decor dispatch
// ---------------------------------------------------------------------------

function drawDecor(c: Ctx, d: Decor, t: number): boolean {
  const { x, y } = d;
  // Shared street life (crowds, cats, cables, signs, food trucks...): `xy_crowd`, `xy_cat`, ...
  if (d.kind.startsWith('xy_') && drawLife(c, d.kind.slice(3), d, t)) return true;
  const night = isNight();
  const N = night ? 'n' : 'd';
  switch (d.kind) {
    case 'xy_station': {
      const p = piece(d);
      art(c, `st:${x}:${N}`, x - 220, p.bottom - 140, 440, 150, (cc) => station(cc, x, p.bottom, night), p.a, p.b);
      return true;
    }
    case 'xy_sysHall': {
      const p = piece(d);
      art(c, `sys:${x}:${N}`, x - 190, p.bottom - 160, 380, 170, (cc) => sysHall(cc, x, p.bottom, night), p.a, p.b);
      return true;
    }
    case 'xy_dept': {
      const p = piece(d);
      const D = DEPTS[p.style];
      art(c, `dept:${p.style}:${x}:${N}`, x - D.w / 2 - 8, p.bottom - D.h - 8, D.w + 16, D.h + 18, (cc) => deptFacade(cc, p.style, x, p.bottom, night), p.a, p.b);
      if (D.screen) {
        const s = D.screen;
        const sy0 = p.bottom + s.y, sy1 = sy0 + s.h;
        const a = Math.max(p.a, sy0), b = Math.min(p.b, sy1);
        if (b > a) {
          c.save();
          c.beginPath();
          c.rect(x + s.x, a, s.w, b - a);
          c.clip();
          screen(c, x + s.x, sy0, s.w, s.h, t, p.style);
          c.restore();
        }
      }
      return true;
    }
    case 'xy_shopBack': {
      const w = d.w ?? 448;
      art(c, `sb:${x}:${y}:${N}`, x - w / 2, y - 24, w, 26, (cc) => shopBack(cc, x - w / 2, y, night));
      return true;
    }
    case 'xy_arcade': {
      const w = d.w ?? 448;
      art(c, `ar:${x}:${y}:${N}`, x - w / 2 - 2, y - 92, w + 4, 94, (cc) => arcade(cc, x - w / 2, y, w, night));
      if (night) for (let i = 0; i < SHOPS.length; i++) ellipse(c, 'rgba(255,220,160,0.08)', x - w / 2 + 40 + i * 72, y - 40, 30, 8);
      return true;
    }
    case 'xy_juancun':
      art(c, `jc:${x}:${y}:${N}`, x - 44, y - 52, 88, 56, (cc) => juancun(cc, x, y, d.variant ?? 0, night));
      return true;
    case 'xy_taxi':
      art(c, `tx:${x}:${y}`, x - 20, y - 24, 40, 28, (cc) => taxi(cc, x, y, (d.variant ?? 0) === 1));
      return true;
    case 'xy_scooterRow': {
      const w = d.w ?? 64;
      art(c, `sr:${x}:${y}`, x - w / 2 - 8, y - 22, w + 16, 26, (cc) => scooterRow(cc, x, y, w, d.variant ?? 0));
      return true;
    }
    case 'xy_scooterWave': {
      const w = d.w ?? 64;
      art(c, `sw:${x}:${y}`, x - w / 2 - 10, y - 44, w + 26, 48, (cc) => scooterWave(cc, x, y, w, d.variant ?? 0));
      return true;
    }
    case 'xy_bus':
      art(c, `bus:${x}:${y}:${N}`, x - 36, y - 30, 72, 34, (cc) => bus(cc, x, y, night));
      return true;
    case 'xy_guard':
      guard(c, x, y);
      return true;
    case 'xy_lotus': {
      const w = d.w ?? 80;
      for (let i = 0; i < 7; i++) {
        const lx = x - w / 2 + 8 + hash(i, 31) * (w - 16), ly = y - 40 + hash(i, 32) * 76;
        ellipse(c, '#3f8a4a', lx, ly, 4.4, 2.6);
        poly(c, '#5aa860', [lx, ly, lx + 4, ly - 1, lx + 4, ly + 1]);
        if (i % 3 === 0) {
          ellipse(c, '#ff9fc0', lx - 1, ly - 2, 1.8, 1.4);
          ellipse(c, '#ffe0ea', lx - 1, ly - 2.6, 0.9, 0.7);
        }
      }
      for (let i = 0; i < 3; i++) {
        const a = t * 0.5 + i * 2.1;
        const kx = x + Math.cos(a) * (w / 2 - 14), ky = y + Math.sin(a * 1.3) * 30;
        const dir = -Math.sin(a) >= 0 ? 1 : -1;
        ellipse(c, ['#ff8a3a', '#ffffff', '#ffd34e'][i], kx, ky, 2.6, 1.1);
        poly(c, ['#ff8a3a', '#ffffff', '#ffd34e'][i], [kx - dir * 2.4, ky, kx - dir * 4.4, ky - 1.2, kx - dir * 4.4, ky + 1.2]);
        if (i === 1) ellipse(c, '#e2493b', kx, ky, 1, 0.6);
      }
      return true;
    }
    case 'xy_bench':
      bench(c, x, y);
      return true;
    case 'xy_vending':
      vending(c, x, y, night);
      return true;
    case 'xy_banyan':
      banyan(c, x, y, t);
      return true;
    case 'xy_garbageTruck':
      garbageTruck(c, x, y, t);
      return true;
    case 'xy_cafeTables':
      cafeTables(c, x, y);
      return true;
    case 'xy_laundry':
      laundry(c, x, y, d.w ?? 14, t);
      return true;
    case 'xy_marketSign':
      marketSign(c, x, y);
      return true;
    case 'xy_pigeons':
      pigeons(c, x, y, t, d.variant ?? 0);
      return true;
    case 'xy_infoBooth':
      infoBooth(c, x, y, night);
      return true;
    case 'xy_bicycle':
      bicycle(c, x, y, d.variant ?? 0);
      return true;
    case 'xy_art':
      publicArt(c, x, y, t);
      return true;
    case 'xy_skyNS':
      skyNS(c, x, y, (d.variant ?? 0) === 2);
      return true;
    case 'xy_skyEW':
      skyEW(c, x, y, d.w ?? 32, (d.variant ?? 0) === 1, night);
      return true;
    case 'xy_skyStairs':
      skyStairs(c, x, y, night);
      return true;
    case 'xy_rock':
      art(c, `rk:${x}:${y}`, x - 22, y - 36, 44, 40, (cc) => rock(cc, x, y, d.variant ?? 0));
      return true;
    case 'xy_railing':
      railing(c, x, y, d.h ?? 96);
      return true;
    case 'xy_stepLamp':
      stepLamp(c, x, y, night);
      return true;
    case 'xy_lineStrip':
      lineStrip(c, x, y, d.w ?? 92);
      return true;
    case 'xy_adWall':
      adWall(c, x, y, d.w ?? 80);
      return true;
    case 'xy_exitBoard':
      exitBoard(c, x, y);
      return true;
    case 'xy_queue':
      queueLines(c, x, y, d.w ?? 200);
      return true;
    case 'xy_mrtName':
      mrtName(c, x, y, d.variant ?? 0);
      return true;
    case 'xy_pillarAd':
      pillarAd(c, x, y, d.variant ?? 0);
      return true;
    default:
      return false;
  }
}

registerPropDrawers({
  objects: OBJ,
  decor: drawDecor,
  animatedObjects: ['xy_love', 'xy_fireworks', 'xy_shrine', 'xy_stage', 'xy_market1', 'xy_market2', 'xy_market3'],
  animatedDecor: ['xy_pigeons', 'xy_dept', 'xy_lotus', 'xy_banyan', 'xy_garbageTruck', 'xy_laundry', ...LIFE_ANIMATED.map((k) => `xy_${k}`)],
});

// ---------------------------------------------------------------------------
// The classic Elephant Mountain photo (drawn into a DOM canvas by content/xinyi.ts)
// ---------------------------------------------------------------------------

export function drawPhoto(c: Ctx, w: number, h: number, mode: 'day' | 'dusk' | 'night'): void {
  const sky = mode === 'day' ? ['#6ab4f0', '#d8f0ff'] : mode === 'dusk' ? ['#5a4a9a', '#ffb070'] : ['#0a1030', '#2a2a5a'];
  vgrad(c, 0, 0, w, h, sky[0], sky[1]);
  if (mode === 'night') for (let i = 0; i < 40; i++) ellipse(c, 'rgba(255,255,255,0.7)', hash(i, 1) * w, hash(i, 2) * h * 0.5, 0.6, 0.6);
  if (mode === 'dusk') ellipse(c, 'rgba(255,220,150,0.8)', w * 0.2, h * 0.62, 18, 18);
  // Far mountains.
  poly(c, mode === 'night' ? '#141a34' : mode === 'dusk' ? '#6a5a8a' : '#8ab0c8', [0, h * 0.62, w * 0.15, h * 0.48, w * 0.32, h * 0.56, w * 0.5, h * 0.44, w * 0.7, h * 0.55, w * 0.88, h * 0.47, w, h * 0.54, w, h * 0.7, 0, h * 0.7]);
  // The city: rows of blocks with lit windows.
  const cityCol = mode === 'night' ? '#1a1e30' : mode === 'dusk' ? '#4a4060' : '#9aa6b4';
  for (let i = 0; i < 46; i++) {
    const bx = (i / 46) * w, bw = w / 46 + 2, bh = 10 + hash(i, 3) * 28;
    rect(c, shade(cityCol, (hash(i, 4) - 0.5) * 0.2), bx, h * 0.78 - bh, bw, bh + h * 0.1);
    if (mode !== 'day') for (let k = 0; k < 6; k++) if (hash(i, k, 5) < 0.5) rect(c, 'rgba(255,220,140,0.85)', bx + 1 + (k % 2) * 3, h * 0.78 - bh + 3 + Math.floor(k / 2) * 5, 1.4, 1.4);
  }
  // Taipei 101: base, eight flared segments, the crown and the spire.
  const cx = w * 0.58, base = h * 0.84;
  const glassL = mode === 'night' ? '#3a6a6a' : '#6fb4ac', glassR = mode === 'night' ? '#244a4a' : '#3a7f82';
  const seg = (y0: number, y1: number, wt: number, wb: number) => {
    poly(c, glassL, [cx - wt / 2, y0, cx, y0, cx, y1, cx - wb / 2, y1]);
    poly(c, glassR, [cx, y0, cx + wt / 2, y0, cx + wb / 2, y1, cx, y1]);
    rect(c, 'rgba(255,255,255,0.3)', cx - wt / 2, y0, wt, 0.6);
  };
  seg(base - 34, base, 22, 28);
  let y = base - 34;
  const dayCol = ['#e2493b', '#ff9a3a', '#ffd34e', '#57c278', '#3a8fd9', '#5a5ad8', '#b06ad8'][new Date().getDay()];
  for (let i = 0; i < 8; i++) {
    seg(y - 13, y, 28, 20);
    if (mode !== 'day') rect(c, dayCol, cx - 14, y - 13, 28, 1);
    y -= 13;
  }
  seg(y - 6, y, 18, 21);
  seg(y - 10, y - 6, 12, 15);
  rect(c, '#b8c6c4', cx - 2, y - 18, 4, 8);
  rect(c, '#9aa8a6', cx - 0.6, y - 34, 1.2, 16);
  if (mode !== 'day') ellipse(c, '#ff5a4a', cx, y - 34, 1.4, 1.4);
  // Foreground: the rocks and trees of Elephant Mountain, and two hikers taking the same photo.
  poly(c, '#2a3a2a', [0, h, 0, h * 0.8, w * 0.12, h * 0.76, w * 0.26, h * 0.86, w * 0.42, h * 0.92, w * 0.42, h]);
  poly(c, '#3a4a38', [w, h, w, h * 0.82, w * 0.86, h * 0.86, w * 0.74, h * 0.94, w * 0.7, h]);
  for (let i = 0; i < 6; i++) ellipse(c, '#1f3a24', w * 0.02 + i * 14, h * 0.8 + (i % 2) * 6, 10, 8);
  ellipse(c, '#2a2420', w * 0.3, h * 0.8, 2.6, 2.8);
  rect(c, '#e2493b', w * 0.3 - 2.6, h * 0.8 + 2.6, 5.2, 8);
  ellipse(c, '#2a2420', w * 0.36, h * 0.81, 2.4, 2.6);
  rect(c, '#3a8fd9', w * 0.36 - 2.4, h * 0.81 + 2.4, 4.8, 7);
  // Film-camera date stamp.
  c.font = '700 10px ui-monospace, monospace';
  c.textAlign = 'right';
  c.textBaseline = 'bottom';
  c.fillStyle = 'rgba(255,150,60,0.9)';
  c.fillText('象山 XIANGSHAN', w - 8, h - 6);
  void OUTLINE;
}
